import React, { useState,useEffect } from "react";
import {
  Search, ChevronDown, Filter, Plus, X, Phone, MapPin, Truck,
  CheckCircle2, Circle, ChevronRight, ChevronLeft, Edit2, Clock,
  Navigation, Building2, User, Bike, Car, Layers, ClipboardList,
  Package, FlaskConical, Cable, ToggleLeft, Zap, Paintbrush, Droplet,Map,
  Grid3x3, Sparkles, ArrowRight, Check, PackageCheck, Trash2,Route,Edit,CheckCircle,XCircle,Globe,Users,CarFront,Warehouse
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../Components/Tabs";
import { Button } from "../../Components/Button";
import { Card } from "../../Components/Card";
import { Badge } from "../../Components/Badge";
import { Input } from "../../Components/Input";
import Pagination, { usePagination } from "../../Components/Pagination";
import { decryptData } from "../../../utils/storageCrypto";


const vehicleTypeIcon = {
  van: "🚐", truck: "🚛", semi: "🚚", bike: "🏍️", container: "🚢",
};


const partnerTypeConfig = {
  "Brickbucket": { label: "Own Fleet", color: "bg-green-100 text-green-700", icon: Truck },
  "3 PL": { label: "3PL", color: "bg-blue-100 text-blue-700", icon: Globe },
  "On Demand": { label: "On Demand", color: "bg-purple-100 text-purple-700", icon: Zap },
  "Supplier": { label: "Supplier", color: "bg-orange-100 text-orange-700", icon: Route },
};

/* ---------------------------------------------------------------------- */
/* Design tokens                                                          */
/* ---------------------------------------------------------------------- */
const ACCENT = "#f7525a";
const ACCENT_SOFT = "#eff6ff";
const SUCCESS = "#16a34a";

const CATEGORY_ICON = {
  "Cement": Package,
  "Construction Chemicals": FlaskConical,
  "Electrical Pipes Fittings & MetalBoxes": Cable,
  "Electrical Switches Switchgear & DB": ToggleLeft,
  "Electrical Wires & Cables": Zap,
  "Paints": Paintbrush,
  "Sanitary Fittings": Droplet,
  "Tile Fixing Products": Grid3x3,
  "Wall Putties & Textures": Layers,
  "Paint Accessories": Sparkles,
};

/* ---------------------------------------------------------------------- */
/* Mock Data                                                              */
/* ---------------------------------------------------------------------- */
const ORDERS = [];

const SHIPMENTS = [];

const TRIPS = [];

const TABS = [
  { key: "orders", label: "Orders", icon: ClipboardList },
  { key: "shipments", label: "Shipments", icon: Package },
  { key: "trips", label: "Trips", icon: Route },
  { key: "fleet", label: "Fleet & Partners", icon: Truck },
];

/* ---------------------------------------------------------------------- */
/* Shared Components                                                      */
/* ---------------------------------------------------------------------- */
function Th({ children, className = "" }) {
  return <th className={`text-left text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap ${className}`}>{children}</th>;
}

function Td({ children, className = "", colSpan, onClick }) {
  return <td colSpan={colSpan} onClick={onClick} className={`px-4 py-3 align-middle text-sm text-slate-700 whitespace-nowrap ${className}`}>{children}</td>;
}

function Pill({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600",
    blue: "bg-blue-50 text-blue-700",
    green: "bg-green-50 text-green-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium tracking-wide ${tones[tone]}`}>{children}</span>;
}

function TopBar({ placeholder, primaryLabel, onPrimary }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-4">
      <div className="flex items-center gap-2 flex-1 max-w-full">
        <div className="relative flex-1 max-w-full">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" placeholder={placeholder} className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400" />
        </div>
        <button className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg bg-white hover:bg-slate-50"><Filter className="w-3.5 h-3.5" /> Filters</button>
      </div>
      <button onClick={onPrimary} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm hover:brightness-110" style={{ backgroundColor: ACCENT }}>
        <Plus className="w-4 h-4" /> {primaryLabel}
      </button>
    </div>
  );
}

function LiveTimeline({ steps }) {
  return (
    <div className="pl-1">
      {steps.map((s, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: s.done ? SUCCESS : "#cbd5e1" }}>
              {s.done && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
            </div>
            {i < steps.length - 1 && <div className="w-px flex-1 min-h-[28px]" style={{ backgroundColor: s.done ? SUCCESS : "#e2e8f0" }} />}
          </div>
          <div className="pb-5 w-full">
            <div className="text-sm font-medium text-slate-800 flex justify-between w-full pr-4">
              <span>{s.title} {s.done && <span className="text-xs font-normal text-green-600">— Completed</span>}</span>
              {s.eta && <span className="text-xs text-blue-600 font-medium whitespace-nowrap ml-2">ETA: {s.eta}</span>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {s.subtext}
              {s.distance && <span className="ml-2 px-1.5 py-0.5 bg-slate-100 rounded text-[10px] text-slate-600">Dist: {s.distance || 0}</span>}
            </div>
            {s.shipments && (
              <div className="mt-1.5 text-xs text-slate-500">
                <span className="font-medium text-slate-600">Shipments Dropping Off:</span> {s.shipments.join(", ")}
              </div>
            )}
            {s.otp && (
              <div className="mt-1.5 inline-block rounded-md bg-gray-100 px-2 py-1 text-xs text-slate-500">
                <span className="font-medium text-slate-600">OTP: {s.otp}</span>
              </div>
            )}
            {/* Renders custom actions like OTP inputs inside the timeline loop */}
            {s.actionNode && (
              <div className="mt-2.5">
                {s.actionNode}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Tracking & Detail Popups                                               */
/* ---------------------------------------------------------------------- */

function OrderTrackPanel({ order, shipments: propShipments, onClose,onNotify }) {
  const [activeTab, setActiveTab] = useState("all");
  const [expandedDGs, setExpandedDGs] = useState(new Set());
  const [expandedShipments, setExpandedShipments] = useState(new Set());

  useEffect(() => {
    if (order) {
      setExpandedDGs(new Set((order.deliveryGroups || []).map(dg => dg.dg || dg.dg_id)));
    }
  }, [order]);

  if (!order) return null;

  const toggleDG = (dgId) => {
    setExpandedDGs(prev => {
      const next = new Set(prev);
      if (next.has(dgId)) next.delete(dgId);
      else next.add(dgId);
      return next;
    });
  };

  const toggleShipment = (sId) => {
    setExpandedShipments(prev => {
      const next = new Set(prev);
      if (next.has(sId)) next.delete(sId);
      else next.add(sId);
      return next;
    });
  };

  // 1. Gather all embedded shipments from DGs
  const embeddedShipmentsMap = {};
  (order.deliveryGroups || []).forEach(dg => {
    (dg.shipments || []).forEach(s => {
      const idKey = s._id || s.id || s.shipment_number;
      if (!embeddedShipmentsMap[idKey]) {
        embeddedShipmentsMap[idKey] = {
          ...s,
          dg: s.dg_number || dg.dg,
          originName: typeof s.origin === 'object' ? s.origin?.name || s.origin?.address : s.origin,
          destName: typeof s.destination === 'object' ? s.destination?.name || s.destination?.address : s.destination
        };
      }
    });
  });

  const orderShipments = Object.values(embeddedShipmentsMap);

  // 2. Flatten Items
  const allItems = order.deliveryGroups?.flatMap(dg => 
    dg.items?.map(item => ({
      ...item,
      dgNumber: dg.dg,
      dgStatus: dg.status,
    })) || []
  ) || [];

  // 3. Group by PO
  const poGroups = allItems.reduce((acc, item) => {
    const poKey = item.po || "PO-UNASSIGNED";
    if (!acc[poKey]) {
      acc[poKey] = {
        poNumber: poKey,
        supplier: item.supplier,
        dgNumber: item.dgNumber,
        status: item.status,
        items: []
      };
    }
    acc[poKey].items.push(item);
    return acc;
  }, {});
  const poList = Object.values(poGroups);

  const isDelivered = order.status?.toLowerCase() === "delivered";
  const isAtHub = order.status?.toLowerCase() === "at hub" || order.status?.toLowerCase() === "at_hub";
  const isInTransit = order.status?.toLowerCase() === "in transit" || isAtHub || isDelivered;

  // Multi-step route generator for a DG
  const getDgRouteSteps = (dg) => {
    const isDgDelivered = dg.status?.toLowerCase() === "delivered";
    const isDgAtHub = dg.status?.toLowerCase() === "at hub" || dg.status?.toLowerCase() === "at_hub";
    const isDgInTransit = dg.status?.toLowerCase() === "in transit" || isDgAtHub || isDgDelivered;
    const isViaHub = dg.route === "Via Hub";

    if (isViaHub) {
      return [
        {
          title: "Supplier Sourcing & Pickup",
          subtext: dg.suppliers || "Supplier Warehouses",
          done: true
        },
        {
          title: "Transit to Hub",
          subtext: dg.distHub ? `Distance: ${dg.distHub}` : "Transit to consolidation hub",
          done: isDgAtHub || isDgDelivered
        },
        {
          title: "Hub Consolidation & Handover",
          subtext: order.pickup || "Central Hub",
          done: isDgAtHub || isDgDelivered
        },
        {
          title: "Hub to Customer Site Transit",
          subtext: dg.distHubToCustomer ? `Distance: ${dg.distHubToCustomer}` : "Final mile transit",
          done: isDgDelivered
        },
        {
          title: "Final Site Delivery",
          subtext: order.dropoff || "Customer Site",
          done: isDgDelivered
        }
      ];
    }

    // Direct Route Steps
    return [
      {
        title: "Supplier Sourcing & Pickup",
        subtext: dg.suppliers || "Supplier Warehouses",
        done: true
      },
      {
        title: "Direct Transit to Customer Site",
        subtext: dg.distCustomer ? `Distance: ${dg.distCustomer}` : "Direct route to destination",
        done: isDgInTransit
      },
      {
        title: "Destination Arrival & Unloading",
        subtext: order.dropoff || "Customer Site",
        done: isDgDelivered
      }
    ];
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl border-l border-slate-200 flex flex-col z-10 animate-in slide-in-from-right duration-300">
        
        {/* Top Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-[#f7525a]">
              <ClipboardList className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-800">{order.orderNumber}</h3>
                <Pill tone={isDelivered ? "green" : isInTransit ? "blue" : "amber"}>{order.status}</Pill>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Ordered on {order.orderedDate ? new Date(order.orderedDate).toLocaleDateString("en-IN") : "N/A"} • Site: {order.dropoff}
              </p>
            </div>
          </div>
{/*           <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button> */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onNotify && onNotify(order)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:text-white hover:bg-red-700 transition-colors"
            >
              Notify Buyer
            </button>
            <button 
              onClick={onClose} 
              className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 px-6 pt-3 pb-2 border-b border-slate-200 bg-slate-50 overflow-x-auto">
          {[
            { key: "all", label: "Overview & All", count: null },
            { key: "dgs", label: "Delivery Groups", count: order.deliveryGroups?.length || 0 },
            { key: "pos", label: "Purchase Orders", count: poList.length },
            { key: "shipments", label: "Shipments", count: orderShipments.length },
            { key: "items", label: "Items", count: allItems.length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                activeTab === tab.key 
                  ? "bg-white text-slate-800 shadow-xs border border-slate-200 font-semibold" 
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                  activeTab === tab.key ? "bg-slate-100 text-slate-700" : "bg-slate-200/70 text-slate-500"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Scrollable Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-6">

          {/* 1. OVERALL ORDER ROUTE MILESTONES */}
          {activeTab === "all" && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] text-slate-400 font-medium">Order ETA</div>
                  <div className="text-xs font-bold text-blue-700 mt-1 truncate">
                    {order.eta ? new Date(order.eta).toLocaleString("en-IN", {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true,
                    }) : "N/A"}
                  </div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] text-slate-400 font-medium">Remaining</div>
                  <div className="text-xs font-bold text-amber-700 mt-1">{order.remaining || "N/A"}</div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] text-slate-400 font-medium">Pickup Node</div>
                  <div className="text-xs font-semibold text-slate-700 mt-1 truncate" title={order.pickup}>{order.pickup}</div>
                </div>
{/*                 <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] text-slate-400 font-medium">Assigned Partner</div>
                  <div className="text-xs font-semibold text-slate-700 mt-1 truncate">{order.partner?.name || "Unassigned"}</div>
                </div> */}
              </div>

              {/* Comprehensive Order Milestones */}
              <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-xs">
                <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-2">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                    <Route className="w-4 h-4 text-slate-500" /> Overall Order Milestones
                  </span>
                  <span className="text-xs text-slate-400">Total Route Progression</span>
                </div>
                <LiveTimeline steps={[
                  { 
                    title: "Order Placed & Confirmed", 
                    subtext: `Order reference ${order.orderNumber}`, 
                    done: true 
                  },
                  { 
                    title: "Supplier Sourcing & PO Dispatch", 
                    subtext: `Coordinating across ${poList.length} POs and ${order.deliveryGroups?.length || 0} DGs`, 
                    done: true 
                  },
                  { 
                    title: "Network Route Movement (Hub / In Transit)", 
                    subtext: isAtHub ? "Consolidated and received at Hub" : isInTransit ? "Dispatched from supplier / on transit legs" : "Awaiting dispatch", 
                    done: isInTransit 
                  },
                  { 
                    title: "Final Site Delivery Handover", 
                    subtext: `Destination: ${order.dropoff}`, 
                    done: isDelivered 
                  },
                ]} />
              </div>
            </>
          )}

          {/* 2. DELIVERY GROUPS (DGs) TRACKING */}
          {(activeTab === "all" || activeTab === "dgs") && (
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <Layers className="w-4 h-4 text-blue-600" /> Delivery Groups ({order.deliveryGroups?.length || 0})
                </div>
              </div>

              <div className="space-y-3">
                {order.deliveryGroups?.map((dg) => {
                  const dgKey = dg.dg || dg.dg_id;
                  const isOpen = expandedDGs.has(dgKey);
                  const dgDelivered = dg.status?.toLowerCase() === "delivered";

                  return (
                    <div key={dgKey} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
                      <div 
                        onClick={() => toggleDG(dgKey)}
                        className="p-3.5 bg-slate-50 hover:bg-slate-100/80 cursor-pointer flex items-center justify-between border-b border-slate-200 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-slate-800">{dg.dg}</span>
                              <Pill tone={dgDelivered ? "green" : "blue"}>{dg.status}</Pill>
                              <span className="text-[11px] px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-medium">
                                {dg.route || "Direct"}
                              </span>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              Suppliers: <span className="text-slate-700">{dg.suppliers || "Multiple Suppliers"}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-semibold text-blue-600">
                            ETA: {dg.eta ? new Date(dg.eta).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit', hour12: true }) : "N/A"}
                          </div>
                          <div className="text-[11px] text-slate-400">
                            {dg.items?.length || 0} line item(s) • {(dg.shipments || []).length} shipment(s)
                          </div>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="p-4 space-y-4">
                          {/* Total DG Route Milestones */}
                          <div>
                            <div className="text-[11px] font-bold text-slate-500 uppercase mb-2">DG Progress Milestones (Complete Route)</div>
                            <LiveTimeline steps={getDgRouteSteps(dg)} />
                          </div>

                          {/* Items nested under this DG */}
                          {(dg.items || []).length > 0 && (
                            <div className="pt-2 border-t border-slate-100">
                              <div className="text-[11px] font-bold text-slate-500 uppercase mb-2">
                                Items in this Delivery Group ({dg.items.length})
                              </div>
                              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                                {dg.items.map((item, idx) => (
                                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100 text-xs">
                                    <div className="flex items-center gap-2">
                                      <Package className="w-3.5 h-3.5 text-slate-400" />
                                      <div>
                                        <span className="font-medium text-slate-800">{item.name}</span>
                                        <span className="text-[10px] text-slate-400 block">PO: {item.po} • Qty: {item.qty} {item.UOM || ''}</span>
                                      </div>
                                    </div>
                                    <Pill tone={item.status === "Delivered" ? "green" : "slate"}>{item.status || "In Transit"}</Pill>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Linked Shipments with Origin -> Destination only */}
                          {(dg.shipments || []).length > 0 && (
                            <div className="pt-2 border-t border-slate-100">
                              <div className="text-[11px] font-bold text-slate-500 uppercase mb-2">Shipments in this DG</div>
                              <div className="space-y-2">
                                {dg.shipments.map(s => {
                                  const sDelivered = s.status?.toLowerCase() === "delivered";
                                  const orig = typeof s.origin === 'object' ? s.origin?.name || s.origin?.address : s.origin;
                                  const dest = typeof s.destination === 'object' ? s.destination?.name || s.destination?.address : s.destination;

                                  return (
                                    <div key={s._id} className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                      <div className="flex justify-between items-center mb-2">
                                        <span className="font-bold text-xs text-slate-800">{s.shipment_number}</span>
                                        <Pill tone={sDelivered ? "green" : "blue"}>{s.status}</Pill>
                                      </div>
                                      <LiveTimeline steps={[
                                        { title: "Origin (Dispatch)", subtext: orig || "Origin", done: true },
                                        { title: "Destination (Arrival)", subtext: dest || "Destination", done: sDelivered }
                                      ]} />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 3. PURCHASE ORDERS (POs) TAB */}
          {(activeTab === "all" || activeTab === "pos") && (
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <ClipboardList className="w-4 h-4 text-purple-600" /> Purchase Orders ({poList.length})
                </div>
              </div>

              {poList.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50 text-slate-400 text-xs">
                  No Purchase Orders linked to this order.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {poList.map((po) => (
                    <div key={po.poNumber} className="border border-slate-200 rounded-xl p-3.5 bg-white shadow-xs">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                            {po.poNumber}
                          </span>
                          <span className="text-xs font-medium text-slate-600">{po.supplier || "Vendor"}</span>
                        </div>
                        <Pill tone={po.status === "Delivered" ? "green" : "blue"}>{po.status || "Dispatched"}</Pill>
                      </div>

                      <div className="text-xs text-slate-500 mb-2">
                        Associated DG: <span className="font-semibold text-slate-700">{po.dgNumber}</span> • Contains {po.items.length} item(s)
                      </div>

                      <div className="bg-slate-50 rounded-lg p-2 border border-slate-100 space-y-1">
                        {po.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center text-xs text-slate-700">
                            <span className="truncate max-w-[280px]">• {item.name}</span>
                            <span className="font-medium text-slate-500">{item.qty} {item.UOM || "units"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 4. SHIPMENTS TAB (Origin & Destination Only) */}
          {(activeTab === "all" || activeTab === "shipments") && (
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <PackageCheck className="w-4 h-4 text-green-600" /> Shipments Linked ({orderShipments.length})
                </div>
              </div>

              {orderShipments.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50 text-slate-400 text-xs">
                  No shipments linked to this order yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {orderShipments.map((s) => {
                    const shipmentKey = s._id || s.id || s.shipment_number;
                    const isShipOpen = expandedShipments.has(shipmentKey);
                    const sDelivered = s.status?.toLowerCase() === "delivered";
                    const orig = s.originName || (typeof s.origin === 'object' ? s.origin?.name : s.origin);
                    const dest = s.destName || (typeof s.destination === 'object' ? s.destination?.name : s.destination);

                    return (
                      <div key={shipmentKey} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
                        <div 
                          onClick={() => toggleShipment(shipmentKey)}
                          className="p-3 bg-slate-50 hover:bg-slate-100 cursor-pointer flex items-center justify-between border-b border-slate-100 transition-colors"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-xs text-slate-800">{s.shipment_number}</span>
                              <Pill tone={sDelivered ? "green" : "blue"}>{s.status}</Pill>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {orig} ➔ {dest}
                            </div>
                          </div>
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isShipOpen ? "rotate-90" : ""}`} />
                        </div>

                        {isShipOpen && (
                          <div className="p-4 space-y-3">
                            <div className="text-[11px] font-bold text-slate-500 uppercase mb-2">Transit Milestones (Origin & Destination)</div>
                            <LiveTimeline steps={[
                              { title: "Origin (Dispatch)", subtext: orig || "Origin Warehouse", done: true },
                              { title: "Destination (Arrival)", subtext: dest || "Destination Site", done: sDelivered },
                            ]} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 5. ITEMIZED TRACKING */}
          {(activeTab === "all" || activeTab === "items") && (
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <Package className="w-4 h-4 text-amber-600" /> Line Items Tracking ({allItems.length})
                </div>
              </div>

              {allItems.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50 text-slate-400 text-xs">
                  No line items found for this order.
                </div>
              ) : (
                <div className="space-y-2">
                  {allItems.map((item, idx) => {
                    const itemDelivered = item.status?.toLowerCase() === "delivered" || isDelivered;
                    return (
                      <div key={idx} className="border border-slate-200 rounded-xl p-3 bg-white hover:bg-slate-50/50 transition-colors shadow-xs">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden mt-0.5">
                              {item.image ? (
                                <img src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${item.image}`} alt={item.name} className="w-full h-full object-cover" />
                              ) : (
                                <Package className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-xs text-slate-800 leading-snug">{item.name}</div>
                              <div className="flex flex-wrap items-center gap-2 mt-1 text-[11px] text-slate-500">
                                <span>PO: <strong className="text-slate-700">{item.po}</strong></span>
                                <span>•</span>
                                <span>DG: <strong className="text-slate-700">{item.dgNumber}</strong></span>
                                <span>•</span>
                                <span>Qty: <strong className="text-slate-700">{item.qty} {item.UOM || ""}</strong></span>
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                Supplier: {item.supplier}
                              </div>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            <Pill tone={itemDelivered ? "green" : "blue"}>{item.status || "In Transit"}</Pill>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
          <div>Tracking for <strong>{order.orderNumber}</strong></div>
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 font-medium hover:bg-slate-100 transition-colors">
            Close
          </button>
        </div>

      </div>
    </div>
  );
}

function DgTrackPanel({ dg, orderInfo, shipments: propShipments, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedShipments, setExpandedShipments] = useState(new Set());

  if (!dg) return null;

  const toggleShipment = (sId) => {
    setExpandedShipments(prev => {
      const next = new Set(prev);
      if (next.has(sId)) next.delete(sId);
      else next.add(sId);
      return next;
    });
  };

  const dgShipments = (dg.shipments && dg.shipments.length > 0)
    ? dg.shipments
    : (propShipments || []).filter(s => s.dg === dg.dg || (Array.isArray(s.dg_ids) && s.dg_ids.includes(dg.dg_id)));

  const isDelivered = dg.status?.toLowerCase() === "delivered";
  const isAtHub = dg.status?.toLowerCase() === "at hub" || dg.status?.toLowerCase() === "at_hub";
  const isInTransit = dg.status?.toLowerCase() === "in transit" || isAtHub || isDelivered;
  const isViaHub = dg.route === "Via Hub";

  // Comprehensive Total Route Steps for Delivery Group
  const dgTotalRouteSteps = isViaHub ? [
    { 
      title: "Supplier Sourcing & Pickup", 
      subtext: dg.suppliers || "Supplier Warehouses", 
      done: true 
    },
    { 
      title: "Supplier to Hub Transit", 
      subtext: dg.distHub ? `Distance: ${dg.distHub}` : "Transit to regional consolidation hub", 
      done: isAtHub || isDelivered 
    },
    { 
      title: "Hub Consolidation & Sort", 
      subtext: orderInfo?.pickup || "Consolidation Hub", 
      done: isAtHub || isDelivered 
    },
    { 
      title: "Hub to Customer Site Transit", 
      subtext: dg.distHubToCustomer ? `Distance: ${dg.distHubToCustomer}` : "Final leg transit", 
      done: isDelivered 
    },
    { 
      title: "Customer Site Arrival & Handover", 
      subtext: orderInfo?.dropoff || "Site Destination", 
      done: isDelivered 
    },
  ] : [
    { 
      title: "Supplier Sourcing & Pickup", 
      subtext: dg.suppliers || "Supplier Warehouse", 
      done: true 
    },
    { 
      title: "Direct Transit to Site", 
      subtext: dg.distCustomer ? `Distance: ${dg.distCustomer}` : "Direct route to destination", 
      done: isInTransit 
    },
    { 
      title: "Destination Arrival & Handover", 
      subtext: orderInfo?.dropoff || "Site Destination", 
      done: isDelivered 
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl border-l border-slate-200 flex flex-col z-10 animate-in slide-in-from-right duration-300">
        
        {/* Top Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-800">{dg.dg}</h3>
                <Pill tone={isDelivered ? "green" : "blue"}>{dg.status}</Pill>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Order: <span className="font-semibold">{orderInfo?.orderNumber}</span> • Routing: {dg.route || "Direct"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 px-6 pt-3 pb-2 border-b border-slate-200 bg-slate-50 overflow-x-auto">
          {[
            { key: "overview", label: "DG Overview" },
            { key: "shipments", label: "Shipments", count: dgShipments.length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                activeTab === tab.key 
                  ? "bg-white text-slate-800 shadow-xs border border-slate-200 font-semibold" 
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-200 text-slate-700">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Scrollable Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-6">

          {/* 1. DG OVERVIEW */}
          {activeTab === "overview" && (
            <>
              {/* Delivery Group Milestones: Total Route Steps */}
              <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-xs">
                <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-2">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                    <Route className="w-4 h-4 text-slate-500" /> Delivery Group Milestones (Total Route)
                  </span>
                  <span className="text-[11px] font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                    Routing: {dg.route || "Direct"}
                  </span>
                </div>
                <LiveTimeline steps={dgTotalRouteSteps} />
              </div>
            </>
          )}

          {/* 2. SHIPMENTS (Origin and Destination only) */}
          {activeTab === "shipments" && (
            <div>
              <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <PackageCheck className="w-4 h-4 text-green-600" /> Shipments Linked ({dgShipments.length})
                </div>
              </div>

              {dgShipments.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50 text-slate-400 text-xs">
                  No shipments mapped to this Delivery Group yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {dgShipments.map((s) => {
                    const sKey = s._id || s.id || s.shipment_number;
                    const isShipOpen = expandedShipments.has(sKey);
                    const sDelivered = s.status?.toLowerCase() === "delivered";
                    const orig = typeof s.origin === 'object' ? s.origin?.name || s.origin?.address : s.origin;
                    const dest = typeof s.destination === 'object' ? s.destination?.name || s.destination?.address : s.destination;

                    return (
                      <div key={sKey} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
                        <div 
                          onClick={() => toggleShipment(sKey)}
                          className="p-3 bg-slate-50 hover:bg-slate-100 cursor-pointer flex items-center justify-between border-b border-slate-100 transition-colors"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-xs text-slate-800">{s.shipment_number}</span>
                              <Pill tone={sDelivered ? "green" : "blue"}>{s.status}</Pill>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {orig} ➔ {dest}
                            </div>
                          </div>
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isShipOpen ? "rotate-90" : ""}`} />
                        </div>

                        {isShipOpen && (
                          <div className="p-4 space-y-3">
                            <div className="text-[11px] font-bold text-slate-500 uppercase mb-2">Transit Milestones (Origin & Destination)</div>
                            <LiveTimeline steps={[
                              { title: "Origin (Dispatch)", subtext: orig || "Origin Node", done: true },
                              { title: "Destination (Arrival)", subtext: dest || "Destination Node", done: sDelivered },
                            ]} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
          <div>Tracking for <strong>{dg.dg}</strong></div>
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 font-medium hover:bg-slate-100 transition-colors">
            Close
          </button>
        </div>

      </div>
    </div>
  );
}


function ShipmentTrackPanel({ shipment, onClose }) {
  if (!shipment) return null;

  const isDelivered = shipment.status?.toLowerCase() === "delivered";
  const orig = typeof shipment.origin === 'object' ? shipment.origin?.name || shipment.origin?.address : shipment.origin;
  const dest = typeof shipment.destination === 'object' ? shipment.destination?.name || shipment.destination?.address : shipment.destination;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full shadow-2xl border-l border-slate-200 flex flex-col z-10 animate-in slide-in-from-right duration-300">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center text-green-600">
              <PackageCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-800">{shipment.shipment_number || shipment.id}</h3>
                <Pill tone={isDelivered ? "green" : "blue"}>{shipment.status}</Pill>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {orig} ➔ {dest}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-6">
          <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-xs">
            <div className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Route className="w-4 h-4 text-slate-500" /> Transit Milestones
            </div>
            {/* Exactly 2 steps: Origin & Destination */}
            <LiveTimeline steps={[
              { 
                title: "Origin (Dispatch)", 
                subtext: orig || "Origin Warehouse / Hub", 
                done: true 
              },
              { 
                title: "Destination (Arrival)", 
                subtext: dest || "Destination Site / Hub", 
                done: isDelivered 
              },
            ]} />
          </div>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 font-medium hover:bg-slate-100 transition-colors">
            Close
          </button>
        </div>

      </div>
    </div>
  );
}


function TripShipmentsModal({ trip, onClose }) {
  if (!trip) return null;
  
  // Aggregate all shipments across all legs
  const associatedShipments = trip.route_legs?.flatMap(leg => leg.shipments || []) || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-xs" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl border border-slate-200 flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <PackageCheck className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-bold text-slate-800">Trip Shipments: {trip.trip_number || trip.id}</span>
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
              {associatedShipments.length} shipment(s)
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        
        <div className="p-6 overflow-y-auto space-y-4">
          {associatedShipments.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-8">No shipments attached to this trip.</div>
          ) : (
            associatedShipments.map((s, sIdx) => {
              const shipmentKey = s._id || s.shipment_number || sIdx;
              const isDelivered = ["delivered", "completed"].includes(s.status?.toLowerCase());
              const orig = typeof s.origin === 'object' ? s.origin?.name || s.origin?.address : s.origin;
              const dest = typeof s.destination === 'object' ? s.destination?.name || s.destination?.address : s.destination;
              const itemsList = s.items || [];

              return (
                <div key={shipmentKey} className="border border-slate-200 rounded-xl p-4 bg-white shadow-xs">
                  {/* Shipment Header */}
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-slate-800">{s.shipment_number || s.so_number || `SHP-${sIdx + 1}`}</span>
                        {s.so_number && <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono">SO: {s.so_number}</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                        <span className="font-medium text-slate-700">{orig || "Origin"}</span> 
                        <ArrowRight className="w-3.5 h-3.5 text-slate-400" /> 
                        <span className="font-medium text-slate-700">{dest || "Destination"}</span>
                      </div>
                    </div>
                    <Pill tone={isDelivered ? "green" : "blue"}>{s.status || "In Transit"}</Pill>
                  </div>

                  {/* Metadata Row */}
                  <div className="flex gap-4 text-xs text-slate-500 py-2 border-y border-slate-100 my-2">
                    <div><span className="text-slate-400">Weight:</span> <strong className="text-slate-700">{typeof s.weight === 'object' ? `${s.weight.value} ${s.weight.unit}` : s.weight || 'N/A'} kg</strong></div>
                    <div><span className="text-slate-400">Volume:</span> <strong className="text-slate-700">{s.volume || 'N/A'}</strong></div>
                    {s.delivery_date && (
                      <div><span className="text-slate-400">ETA / Date:</span> <strong className="text-slate-700">{new Date(s.delivery_date).toLocaleDateString("en-IN")}</strong></div>
                    )}
                  </div>

                  {/* Items list inside this shipment */}
                  <div className="mt-2">
                    <div className="text-[11px] font-bold text-slate-500 uppercase mb-1.5">Items in this Shipment ({itemsList.length})</div>
                    {itemsList.length === 0 ? (
                      <div className="text-xs text-slate-400 italic">No line item breakdown available.</div>
                    ) : (
                      <div className="space-y-1.5">
                        {itemsList.map((item, iIdx) => (
                          <div key={item._id || iIdx} className="flex justify-between items-center p-2 rounded-lg bg-slate-50 border border-slate-100 text-xs">
                            <div className="flex items-center gap-2">
                              <Package className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              <div>
                                <span className="font-semibold text-slate-800">{item.itemName || item.name}</span>
                                {item.po_number && <span className="text-[10px] text-slate-400 block">PO: {item.po_number}</span>}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-slate-700">Qty: {item.quantity || item.qty || 1}</span>
                              {(item.itemWeight || item.weight) && (
                                <span className="text-[10px] text-slate-400 block">{item.itemWeight || item.weight} kg</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


function TripRouteLegsModal({ trip, onClose }) {
  const [otps, setOtps] = useState({});

  if (!trip) return null;

  const isManual3PL = trip.status === "3pl_assigned" || (trip.platform && !["brickbucket", "own"].includes(trip.platform.toLowerCase()));

  const handleOtpChange = (stopId, val) => {
    setOtps(prev => ({ ...prev, [stopId]: val }));
  };

  const handleVerifyOtp = (stopId, stopType) => {
    alert(`${stopType} OTP verification triggered: ${otps[stopId]}`);
  };

  const formatEta = (time) => {
    if (!time) return "TBD";
    const d = new Date(time);
    return isNaN(d.getTime()) ? time : d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true
    });
  };

  // Flatten nested DB stops with resilient ETA & status completion checks
  const steps = trip.route_legs?.flatMap((leg, legIdx) => {
    const isLegDone = ["completed", "delivered"].includes(leg.status?.toLowerCase());
    const isTripDone = ["completed", "delivered"].includes(trip.status?.toLowerCase());

    return (leg.stops || []).map((stop, idx) => {
      const stopId = stop._id || `${leg._id || legIdx}-stop-${idx}`;
      const stopType = (stop.stopType || stop.stop_type || "WAYPOINT").toUpperCase();
      const isPickup = stopType === 'PICKUP';
      const isDropoff = stopType === 'DROPOFF';

      // Check if stop is completed directly, by sequence progress, or if whole leg/trip is completed
      const isStopCompleted = 
        isTripDone ||
        isLegDone ||
        ["completed", "done", "delivered", "visited"].includes(stop.status?.toLowerCase()) ||
        stop.isCompleted === true;

      const showOtp = isManual3PL && !isStopCompleted && (isPickup || isDropoff);

      const stopEta = formatEta(stop.plannedTime || stop.planned_time || stop.eta || stop.estimated_time || leg.estimated_arrival_time);
      const stopAddress = stop.location?.address || stop.address || (typeof stop.location === 'string' ? stop.location : "Location pending");

      return {
        title: `Stop ${stop.stopSequence || stop.sequence || idx + 1}: ${stopType}`,
        subtext: stopAddress,
        distance: leg.distance ? `${leg.distance} km` : "0 km",
        eta: stopEta,
        done: isStopCompleted,
        otp: stop.otp || undefined,
        actionNode: showOtp ? (
           <div className="flex gap-2 max-w-sm mt-1.5 p-2 bg-slate-50 rounded-lg border border-slate-200">
              <input 
                type="text" 
                placeholder={`Enter ${isPickup ? 'Pickup' : 'Drop-off'} OTP`} 
                value={otps[stopId] || ""} 
                onChange={(e) => handleOtpChange(stopId, e.target.value)}
                className="w-full text-xs rounded border border-slate-300 px-2 py-1.5 outline-none focus:border-blue-500 bg-white"
              />
              <button onClick={() => handleVerifyOtp(stopId, stopType)} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 whitespace-nowrap transition-colors">
                Verify
              </button>
           </div>
        ) : null
      };
    });
  }) || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl border border-slate-200 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="text-sm font-semibold text-slate-800">Trip Route Legs: {trip.trip_number || trip.id}</div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto">
          {steps.length > 0 ? <LiveTimeline steps={steps} /> : <div className="text-sm text-slate-500">No routing stops generated yet.</div>}
        </div>
      </div>
    </div>
  );
}

function TripTrackModal({ trip, onClose }) {
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "route" | "shipments"
  const [apiTrackingData, setApiTrackingData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // State for holding per-stop OTP values
  const [otps, setOtps] = useState({});

  useEffect(() => {
    if (trip) {
      const tripId = trip._id || trip.id;
      if (tripId) {
        fetchTripTracking(tripId);
      }
    }
  }, [trip]);

  const fetchTripTracking = async (tripId) => {
    try {
      setIsLoading(true);
      const token = decryptData(sessionStorage.getItem("token"));
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/get-trip-tracking?trip_id=${tripId}`,
        {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json',
            'x-token': token || ""
          }
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setApiTrackingData(result.data);
        }
      }
    } catch (error) {
      console.warn("Using fallback local trip tracking data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!trip) return null;

  // 1. Data Extraction & Formatting
  const totalShipments = trip.route_legs?.reduce((acc, leg) => acc + (leg.shipments?.length || 0), 0) || 0;
  const displayDistance = trip.totalDistance ? `${trip.totalDistance} km` : 0;
  const displayDuration = trip.totalEstimatedMinutes ? `${trip.totalEstimatedMinutes} mins` : "N/A";
  
  const tripDelivered = trip.status?.toLowerCase() === "delivered";
  const tripInTransit = trip.status?.toLowerCase() === "in_transit" || trip.status?.toLowerCase() === "dispatched" || tripDelivered;

  // Aggregate all unique shipments across all legs using a plain object to avoid 'new Map()' collision with the lucide-react Map icon
  const uniqueShipmentsObj = {};
  trip.route_legs?.forEach(leg => {
    leg.shipments?.forEach(s => {
      const sId = s._id || s.id || s;
      if (!uniqueShipmentsObj[sId]) {
        uniqueShipmentsObj[sId] = typeof s === 'object' ? s : { _id: s, shipment_number: s };
      }
    });
  });
  const allTripShipments = Object.values(uniqueShipmentsObj);

  const isManual3PL = trip?.status === "3pl_assigned" || (trip?.platform && !["brickbucket", "own"].includes(trip.platform.toLowerCase()));

  const handleOtpChange = (stopId, val) => {
    setOtps(prev => ({ ...prev, [stopId]: val }));
  };

  const handleVerifyOtp = (stopId, stopType) => {
    console.log(`Verifying ${stopType} OTP for stop ${stopId}:`, otps[stopId]);
    alert(`${stopType} OTP verification triggered: ${otps[stopId]}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-xs transition-opacity" onClick={onClose} />

      {/* Drawer Panel */}
      <div className="relative w-full max-w-2xl bg-white h-full shadow-2xl border-l border-slate-200 flex flex-col z-10 animate-in slide-in-from-right duration-300">
        
        {/* Top Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600">
              <Route className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-800">{trip.trip_number || trip.id}</h3>
                <Pill tone={tripDelivered ? "green" : tripInTransit ? "blue" : "slate"}>{trip.status}</Pill>
                {trip.type && <span className="text-[10px] uppercase font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded">{trip.type}</span>}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Driver: <span className="font-semibold text-slate-700">{trip.partner_id?.name || "Unassigned"}</span> • {trip.partner_id?.vehicle_type || "Vehicle TBD"}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-2 px-6 pt-3 pb-2 border-b border-slate-200 bg-slate-50 overflow-x-auto">
          {[
            { key: "overview", label: "Trip Overview" },
            { key: "route", label: "Route & Stops", count: trip.route_legs?.length || 0 },
            { key: "shipments", label: "Shipments", count: allTripShipments.length },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                activeTab === tab.key 
                  ? "bg-white text-slate-800 shadow-xs border border-slate-200 font-semibold" 
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                  activeTab === tab.key ? "bg-slate-100 text-slate-700" : "bg-slate-200/70 text-slate-500"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Scrollable Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-6">

          {/* 1. OVERVIEW TAB */}
          {(activeTab === "overview") && (
            <>
              <div className="grid grid-cols-4 gap-3">
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] text-slate-400 font-medium">Trip ETA</div>
                  <div className="text-xs font-bold text-blue-700 mt-1 truncate">{trip.estimated_delivery_time || trip.eta || "Pending"}</div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] text-slate-400 font-medium">Est. Distance</div>
                  <div className="text-xs font-bold text-slate-700 mt-1">{displayDistance || 0}</div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] text-slate-400 font-medium">Est. Duration</div>
                  <div className="text-xs font-semibold text-slate-700 mt-1">{displayDuration}</div>
                </div>
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="text-[11px] text-slate-400 font-medium">Total Shipments</div>
                  <div className="text-xs font-semibold text-slate-700 mt-1">{totalShipments}</div>
                </div>
              </div>

              {trip.partner_id && (
                <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-xs flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <User className="w-6 h-6 text-slate-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-bold text-slate-800">{trip.partner_id.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                      {/*<span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {trip.partner_id.mobile || trip.partner_id.contact_number}</span>*/}
                      <button 
                        onClick={(e) => { 
                            e.stopPropagation(); 
                            // Dispatch custom event to bubble up or pass a direct prop
                            // Assuming onPhoneClick is added to TripTrackModal props:
                            if(window.handleGlobalPhoneClick) window.handleGlobalPhoneClick(trip.partner_id.mobile || trip.partner_id.contact_number);
                        }} 
                        className="flex items-center gap-1 text-blue-600 hover:underline cursor-pointer"
                      >
                         <Phone className="w-3 h-3" /> {trip.partner_id.mobile || trip.partner_id.contact_number}
                      </button>
                      <span>•</span>
                      <span className="flex items-center gap-1"><CarFront className="w-3 h-3" /> {trip.partner_id.vehicle_type} ({trip.partner_id.vehicle_number || "Reg TBD"})</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* 2. ROUTE & STOPS TAB (Detailed Breakdown) */}
          {(activeTab === "route") && (
            <div>
              <div className="flex items-center justify-between mb-4 border-b border-slate-200 pb-2">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <Map className="w-4 h-4 text-orange-600" /> Route Execution Plan
                </div>
              </div>

              {(!trip.route_legs || trip.route_legs.length === 0) ? (
                <div className="p-6 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50 text-slate-400 text-xs">
                  No routing stops or legs generated for this trip yet.
                </div>
              ) : (
                <div className="space-y-6">
                  {trip.route_legs.map((leg, legIdx) => {
                    
                    // Map shipments for this specific leg
                    const legShipments = leg.shipments?.map(s => {
                      if (typeof s === 'object') return s.shipment_number || s.so_number || s._id;
                      return s;
                    }) || [];

                    // Map the stops to fit the LiveTimeline component structure
/*                     const timelineSteps = leg.stops?.map(stop => ({
                      title: `Stop ${stop.stopSequence || ''}: ${stop.stopType || 'Waypoint'}`,
                      subtext: stop.location?.address || "Location pending",
                      eta: stop.plannedTime ? new Date(stop.plannedTime).toLocaleString("en-IN", { hour: '2-digit', minute: '2-digit', hour12: true, month: 'short', day: 'numeric' }) : "TBD",
                      done: stop.status === 'COMPLETED' || stop.status === 'SKIPPED',
                      distance: leg.distance ? `${leg.distance} km total leg` : undefined
                    })) || []; */

                    // Map the stops to fit the LiveTimeline component structure
                    const timelineSteps = leg.stops?.map((stop, idx) => {
                      const stopId = stop._id || `${leg._id || legIdx}-stop-${idx}`;
                      const isPickup = stop.stopType === 'PICKUP';
                      const isDropoff = stop.stopType === 'DROPOFF';
                      const showOtp = isManual3PL && stop.status !== 'completed' && stop.status !== 'skipped' && (isPickup || isDropoff);

                      return {
                        title: `Stop ${stop.stopSequence || ''}: ${stop.stopType || 'Waypoint'}`,
                        subtext: stop.location?.address || "Location pending",
                        eta: stop.plannedTime ? new Date(stop.plannedTime).toLocaleString("en-IN", { hour: '2-digit', minute: '2-digit', hour12: true, month: 'short', day: 'numeric' }) : "TBD",
                        done: stop.status === 'completed' || stop.status === 'skipped',
                        distance: leg.distance ? `${leg.distance} km total leg` : 0,
                        actionNode: showOtp ? (
                           <div className="flex gap-2 max-w-sm mt-1.5 p-2 bg-slate-50 rounded-lg border border-slate-200">
                              <input 
                                type="text" 
                                placeholder={`Enter ${isPickup ? 'Pickup' : 'Drop-off'} OTP`} 
                                value={otps[stopId] || ""} 
                                onChange={(e) => handleOtpChange(stopId, e.target.value)}
                                className="w-full text-xs rounded border border-slate-300 px-2 py-1.5 outline-none focus:border-blue-500 bg-white"
                              />
                              <button onClick={() => handleVerifyOtp(stopId, stop.stopType)} className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 whitespace-nowrap transition-colors">
                                Verify
                              </button>
                           </div>
                        ) : null
                      };
                    }) || [];

                    return (
                      <div key={leg._id || legIdx} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                        {/* Leg Header */}
                        <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                          <div>
                            <div className="text-sm font-bold text-slate-800">
                              Route Leg {leg.sequence || legIdx + 1}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                              <span>Type: <strong className="text-slate-700 capitalize">{leg.type ? leg.type.replace(/_/g, ' ') : 'Standard'}</strong></span>
                              {leg.distance && (
                                <>
                                  <span>•</span>
                                  <span>Distance: <strong className="text-slate-700">{leg.distance || 0} km</strong></span>
                                </>
                              )}
                            </div>
                          </div>
                          <Pill tone={leg.status === 'completed' ? 'green' : leg.status === 'in_transit' ? 'blue' : 'slate'}>
                            {leg.status || 'Pending'}
                          </Pill>
                        </div>

                        <div className="p-4">
                          {/* Shipments carried in this specific leg */}
                          <div className="mb-5">
                            <div className="text-[10px] font-bold text-slate-500 uppercase mb-2">Shipments Transported in this Leg</div>
                            {legShipments.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {legShipments.map((sNum, sIdx) => (
                                  <span key={sIdx} className="px-2.5 py-1 bg-blue-50 border border-blue-100 text-blue-700 rounded text-[11px] font-semibold flex items-center gap-1.5">
                                    <PackageCheck className="w-3 h-3" /> {sNum}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <div className="text-xs text-slate-400">No shipments linked directly to this leg.</div>
                            )}
                          </div>

                          {/* Stops Timeline for this leg */}
                          <div>
                            <div className="text-[10px] font-bold text-slate-500 uppercase mb-2">Stops & Waypoints</div>
                            {timelineSteps.length > 0 ? (
                              <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 pt-4 pb-0">
                                <LiveTimeline steps={timelineSteps} />
                              </div>
                            ) : (
                              <div className="text-xs text-slate-400 border border-slate-100 rounded-lg p-3 bg-slate-50">No stops defined.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 3. SHIPMENTS TAB */}
          {(activeTab === "shipments") && (
            <div>
              <div className="flex items-center justify-between mb-4 border-b border-slate-200 pb-2">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                  <PackageCheck className="w-4 h-4 text-green-600" /> All Shipments on Trip ({allTripShipments.length})
                </div>
              </div>

              {allTripShipments.length === 0 ? (
                <div className="p-6 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50 text-slate-400 text-xs">
                  No shipments attached to this trip.
                </div>
              ) : (
                <div className="space-y-3">
                  {allTripShipments.map((s, idx) => (
                    <div key={s._id || idx} className="border border-slate-200 rounded-xl p-3.5 bg-white shadow-xs flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                          <PackageCheck className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-800">{s.shipment_number || s.so_number || s._id}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                            {s.origin?.name && <span>Origin: <strong className="text-slate-700">{s.origin.name}</strong></span>}
                            {s.origin?.name && s.destination?.name && <span>•</span>}
                            {s.destination?.name && <span>Dest: <strong className="text-slate-700">{s.destination.name}</strong></span>}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            Weight: {s.weight || 'N/A'} {s.weight ? 'kg' : ''} • Vol: {s.volume || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <Pill tone={s.status === 'Delivered' ? 'green' : 'blue'}>{s.status || 'In Transit'}</Pill>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* Drawer Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
          <div>
            Viewing detailed telemetry for Trip <strong className="text-slate-700">{trip.trip_number || trip.id}</strong>
          </div>
          <button 
            onClick={onClose} 
            className="px-4 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 font-medium hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Create Trip Modal (Generated from Shipments)                           */
/* ---------------------------------------------------------------------- */
function CreateTripModal({ shipmentsData,selectedShipmentIds, onClose, onRefresh,showPopup }) {
  const shipments = shipmentsData.filter(s => selectedShipmentIds.has(s.id));
  
  const generatedRouteLegs = shipments.map((s, index) => ({
      title: `Waypoint ${index + 1}: ${s.destination}`,
      subtext: `Drop off for ${s.id}`,
      distance: `${(index + 1) * 12.5} km`,
      eta: `13 Sep, 1${index + 2}:00`,
      done: false,
      shipments: [s.id]
  }));

  const originLeg = {
      title: "Depart Origin",
      subtext: "Auto Nagar Hub",
      distance: "0 km",
      eta: "13 Sep, 10:00",
      done: false,
      shipments: shipments.map(s => s.id)
  };

  const legs = [originLeg, ...generatedRouteLegs];


const createOrUpdateTrip = async (tripData) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-trip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tripData)
    });
    
    const result = await response.json();
    if (result.success || response.ok) {
      if (onRefresh) onRefresh();
      showPopup("Trip created successfully!", "success", "trips");
      onClose();
    } else {
      showPopup(result.message || "Failed to create trip", "error");
    }
  } catch (error) {
    console.error("Error in createOrUpdateTrip:", error);
    showPopup("An error occurred while creating the trip", "error");
    throw error;
  }
}; 


const handleConfirmTrip = async () => {
    try {
      // 1. Just extract the IDs from the selected shipments
      const shipmentIds = shipments.map(s => s._id || s.id);

      const payload = {
        shipment_ids: shipmentIds
      };

      const token = decryptData(sessionStorage.getItem("token"));
      
      // 2. Point to the new API endpoint
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-trip-from-shipments`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-token': token || ""
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      if (result.success || response.ok) {
        if (onRefresh) onRefresh();
        showPopup("Trip created successfully!", "success", "trips");
        onClose();
      } else {
        showPopup(result.message || "Failed to create trip", "error");
      }
    } catch (error) {
      console.error("Error creating trip:", error);
      showPopup("An error occurred while creating the trip", "error");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-xl shadow-xl border border-slate-200 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="text-sm font-semibold text-slate-800">Review & Create Trip</div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          <div className="text-sm font-medium text-slate-800 mb-4">Generated Route Legs</div>
          <div className="border border-slate-200 rounded-lg p-5 bg-slate-50">
             <LiveTimeline steps={legs} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
          <button onClick={handleConfirmTrip} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: ACCENT }}>
          Confirm & Create Trip
          </button>
      </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Create Shipment Modal with Item Removal                                */
/* ---------------------------------------------------------------------- */
function CreateShipmentModal({ initialItems, onClose, onRefresh,showPopup }) {
  const [items, setItems] = useState(initialItems);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const removeItem = (uniqueKey) => setItems(prev => prev.filter(item => item.uniqueKey !== uniqueKey));

  // Determine Origin & Destination from the first item in the shipment list
  const refItem = items[0] || {};
  console.log("refItem",refItem)
  
  const isAtHub = refItem.isAtHub || refItem.dgStatus === 'at hub' || refItem.status === 'at hub';

  // Default route: if items are at hub, the route is direct to customer
  const [route, setRoute] = useState(isAtHub ? "Direct" : (initialItems[0]?.route || "Via Hub"));


  // 1. Dynamic Origin Calculation
  const dynamicOrigin = isAtHub ? {
    // When at HUB: Origin is the Hub
    name: refItem.hubName || "Hub",
    address: refItem.hubAddress || "Hub address not provided",
    latitude: refItem.hubLat,
    longitude: refItem.hubLng,
    mobile: refItem.hubMobile,
    type: 'HUB'
  } : {
    // Standard forward flow: Origin is the Supplier
    name: refItem.supplier || refItem.supplierName,
    address: refItem.supplierAddress || "Address not provided",
    latitude: refItem.supplierLat,
    longitude: refItem.supplierLng,
    mobile: refItem.supplierMobile,
    type: refItem.supplierType || 'SUPPLIER'
  };

  // 2. Dynamic Destination Calculation
  const dynamicDestination = isAtHub ? {
    // When at HUB: Destination is the final Customer Site
    name: refItem.customerName || "Customer Site",
    address: refItem.customerAddress || "Customer address not provided",
    latitude: refItem.customerLat,
    longitude: refItem.customerLng,
    mobile: refItem.customerMobile,
    type: 'BUYER'
  } : (route === "Via Hub" ? {
    // Pre-hub flow routed via hub
    name: refItem.hubName || "Hub",
    address: refItem.hubAddress || "Hub address not provided",
    latitude: refItem.hubLat,
    longitude: refItem.hubLng,
    mobile: refItem.hubMobile,
    type: refItem.hubType || 'HUB'
  } : {
    // Direct from supplier to customer
    name: refItem.customerName || "Customer Site",
    address: refItem.customerAddress || "Customer address not provided",
    latitude: refItem.customerLat,
    longitude: refItem.customerLng,
    mobile: refItem.customerMobile,
    type: refItem.customerType || 'BUYER'
  });

/*   const dynamicOrigin = {
    name: refItem.supplier || refItem.supplierName,
    address: refItem.supplierAddress || "Address not provided",
    latitude: refItem.supplierLat,
    longitude: refItem.supplierLng,
    mobile:refItem.supplierMobile,
    type:refItem.supplierType
  };

  const dynamicDestination = route === "Via Hub" ? {
    name: refItem.hubName || "Hub",
    address: refItem.hubAddress || "Hub address not provided",
    latitude: refItem.hubLat,
    longitude: refItem.hubLng,
    mobile: refItem.hubMobile,
    type: refItem.hubType
  } : {
    name: refItem.customerName || "Customer Site",
    address: refItem.customerAddress || "Customer address not provided",
    latitude: refItem.customerLat,
    longitude: refItem.customerLng,
    mobile: refItem.customerMobile,
    type: refItem.customerType
  }; */

  const createOrUpdateShipment = async (shipmentData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-shipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shipmentData)
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error("Error in createOrUpdateShipment:", error);
      throw error;
    }
  };

  // Helper to extract numbers from weight strings (e.g., "12,000 kg" -> 12000)
  const parseNumber = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    const num = String(val).replace(/,/g, '').match(/[\d.]+/);
    return num ? parseFloat(num[0]) : 0;
  };

  const handleConfirmShipment = async () => {
    setIsSubmitting(true);

    try {
      // 1. Extract unique Order Numbers and DG references
      const so_number = [...new Set(items.map(i => i.orderNumber))].join(", ");
      
      // Note: If you have actual MongoDB ObjectIds for DGs, map them here. 
      // Falling back to dgNumber if the ID isn't directly available on the item.
      const dg_ids = [...new Set(items.map(i => i.dg_id || i.dgNumber))];
      const po_ids = [...new Set(items.map(i => i.po_id || i.po))];

      // 2. Format items to match ShipmentSchema
      const formattedItems = items.map(item => ({
        itemId: item.id,
        quantity: item.qty,
        itemName: item.name,
        itemImage: item.image,
        dimensions: typeof item.dimensions === 'object' ? item.dimensions : { length: 0, width: 0, height: 0, unit: 'cm' },
        itemWeight: parseNumber(item.weight?.value || item.weight),
        po_number: item.po,
        so_number: item.orderNumber,
        dg_number: item.dgNumber,
        shipment_image:item.packed_image
      }));

      // 3. Calculate total weight & volume
      const totalWeight = items.reduce((sum, item) => sum + parseNumber(item.weight?.value || item.weight), 0);
      const totalVolume = items.reduce((sum, item) => sum + parseNumber(item.volume), 0);

      // 4. Construct the payload
      const payload = {
        so_number: so_number,
        dg_ids: dg_ids,
        po_ids: po_ids,
        delivery_date: new Date().toISOString(), // Or a date selected from the UI
        status: "Pending",
        items: formattedItems,
        weight: totalWeight,
        volume: totalVolume,
        origin: dynamicOrigin,
        destination: dynamicDestination,
        shipment_image:formattedItems[0]?.shipment_image
      };

      // 5. Fire API
      const result = await createOrUpdateShipment(payload);

      console.log("create shipment:", result);
      
      if (result.success) {
        if (onRefresh) onRefresh();
        showPopup("Shipment created successfully!", "success", "shipments");
        onClose();
      } else {
        showPopup(result.message || "Failed to create shipment", "error");
      }
    } catch (error) {
      showPopup("Error: " + error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl border border-slate-200 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="text-sm font-semibold text-slate-800">Create Shipment</div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-3 gap-4 mb-4">
            
            {/* Dynamic Origin Display (FIXED OBJECT RENDERING) */}
            <div className="border border-slate-200 p-3 rounded-lg bg-slate-50">
              <div className="text-xs text-slate-500 font-medium mb-1">Origin ({dynamicOrigin.name})</div>
              <div className="text-sm font-medium truncate" title={dynamicOrigin.address}>{dynamicOrigin.address}</div>
            </div>
            
            {/* Dynamic Destination Display (FIXED OBJECT RENDERING) */}
            <div className="border border-slate-200 p-3 rounded-lg bg-slate-50">
              <div className="text-xs text-slate-500 font-medium mb-1">Destination ({dynamicDestination.name})</div>
              <div className="text-sm font-medium truncate" title={dynamicDestination.address}>{dynamicDestination.address}</div>
            </div>
            
            {/* Route Selector Updates UI State Immediately */}
{/*             <div className="border border-slate-200 p-3 rounded-lg bg-slate-50">
              <div className="text-xs text-slate-500 mb-1 font-medium">Recommended Route</div>
              <select 
                value={route} 
                onChange={(e) => setRoute(e.target.value)} 
                className="w-full text-sm bg-white border border-slate-300 rounded outline-none p-1 font-medium text-blue-600"
              >
                <option value="Via Hub">Via Hub</option>
                <option value="Direct">Direct</option>
              </select>
            </div> */}
            <div className="border border-slate-200 p-3 rounded-lg bg-slate-50">
            <div className="text-xs text-slate-500 mb-1 font-medium">Recommended Route</div>
            {isAtHub ? (
              <div className="text-sm font-semibold text-blue-700 py-1">Hub to Customer (Direct)</div>
            ) : (
              <select 
                value={route} 
                onChange={(e) => setRoute(e.target.value)} 
                className="w-full text-sm bg-white border border-slate-300 rounded outline-none p-1 font-medium text-blue-600"
              >
                <option value="Via Hub">Via Hub</option>
                <option value="Direct">Direct</option>
              </select>
            )}
          </div>
          </div>
          
          <div className="flex justify-between items-center mb-2">
             <div className="text-sm font-medium text-slate-800">Items in Shipment ({items.length})</div>
          </div>
          
          <div className="border border-slate-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
            {items.length === 0 ? (
               <div className="p-4 text-sm text-slate-500 text-center">No items selected.</div>
            ) : (
              items.map((item) => (
                <div key={item.uniqueKey} className="flex justify-between items-center px-4 py-3 border-b border-slate-100 last:border-b-0 text-sm text-slate-700">
                  <div>
                    <div className="font-medium">{item.name}</div>
                    <div className="text-xs text-slate-500">
                      PO: {item.po} | DG: {item.dgNumber} | Qty: {item.qty} | Supplier: {item.supplier}
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.uniqueKey)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors">
                     <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm text-slate-600 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          
          <button 
            disabled={items.length === 0 || isSubmitting} 
            onClick={handleConfirmShipment} 
            className="px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 flex items-center gap-2" 
            style={{ backgroundColor: "#f7525a" }}
          >
            {isSubmitting ? "Creating..." : "Confirm Shipment"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Screen 1 — Orders                                                      */
/* ---------------------------------------------------------------------- */
function OrdersScreen({ orders, loading, onTrackOrder, onTrackDg,onRefresh,showPopup,onNotifyOrder }) {
  const [expandedOrderIds, setExpandedOrderIds] = useState([]);
  const [expandedDgIds, setExpandedDgIds] = useState([]);
  const [selectedItemKeys, setSelectedItemKeys] = useState(new Set()); // Uses composite keys now
  const [showCreateShipment, setShowCreateShipment] = useState(false);

  const {
    currentItems: paginatedOrders,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
  } = usePagination(orders || [], 5);

  // Helper to generate a globally unique key for each line item
  const getItemKey = (orderNum, dgNum, poNum, itemId) => {
    return `${orderNum}_${dgNum}_${poNum}_${itemId}`;
  };

  const toggleOrder = (id) => {
    setExpandedOrderIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  
  const toggleDg = (id) => {
    setExpandedDgIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectDg = (dg, order, checked) => {
    const newSel = new Set(selectedItemKeys);
    dg.items.forEach(item => {
      const itemDelivered = item.status?.toLowerCase() === "delivered" || dg.status?.toLowerCase() === "delivered";
      // Only allow selection of items that are NOT delivered
      if (!itemDelivered) {
        const key = getItemKey(order.orderNumber, dg.dg, item.po, item.id);
        if (checked) newSel.add(key); else newSel.delete(key);
      }
    });
    setSelectedItemKeys(newSel);
  };

  const handleSelectItem = (itemKey, checked) => {
    const newSel = new Set(selectedItemKeys);
    if (checked) newSel.add(itemKey); else newSel.delete(itemKey);
    setSelectedItemKeys(newSel);
  };

  const isDgFullySelected = (dg, order) => {
    const selectableItems = dg.items.filter(item => !(item.status?.toLowerCase() === "delivered" || dg.status?.toLowerCase() === "delivered"));
    if (selectableItems.length === 0) return false; // If everything is delivered, checkbox is technically unchecked/disabled

    return selectableItems.every(item => {
      const key = getItemKey(order.orderNumber, dg.dg, item.po, item.id);
      return selectedItemKeys.has(key);
    });
  };

  const getSelectedItemsData = () => {
    const items = [];
    orders.forEach(order => {
      order.deliveryGroups.forEach(dg => {
        dg.items.forEach(item => {
          const key = getItemKey(order.orderNumber, dg.dg, item.po, item.id);
          if (selectedItemKeys.has(key)) {
            items.push({
              ...item,
              uniqueKey: key, 
              orderNumber: order.orderNumber,
              orderStatus: order.status,
              dgNumber: dg.dg,
              dgStatus: dg.status,
              dg_id: dg.dg_id,
              isAtHub: dg.status === 'At Hub' || order.status === 'At Hub'
            });
          }
        });
      });
    });
    return items;
  };

  return (
    <div className="flex flex-col mb-10">
      <TopBar placeholder="Search orders, items..." primaryLabel="Create Shipment" onPrimary={() => {
        if (selectedItemKeys.size > 0) setShowCreateShipment(true);
        else alert("Select items first");
      }} />
      
      <div className="border border-slate-200 rounded-xl bg-white flex flex-col shadow-sm w-full">
        <div className="overflow-auto max-h-[calc(100vh-380px)] min-h-[300px] flex-1">
          <table className="w-full min-w-[1200px] border-collapse relative">
            <colgroup>
              <col style={{ width: "350px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "100px" }} />
              <col style={{ width: "100px" }} />
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ):paginatedOrders?.length === 0 ? (
              <tr>
                  <td colSpan={9} className="py-16 text-center text-gray-500 border border-gray-100 rounded-lg">
                    <div className="flex flex-col items-center justify-center w-full">
                      <Package className="w-8 h-8 mb-2 text-gray-400" />
                      <p className="text-sm font-medium">No Orders Found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedOrders?.map((order) => {
                  const isOrderOpen = expandedOrderIds.includes(order.orderNumber);
                  return (
                    <React.Fragment key={order.orderNumber}>
                      {/* Order Level Row */}
                      <tr className="bg-slate-50 border-b border-slate-200 hover:bg-slate-100 cursor-pointer sticky top-0 z-10" onClick={() => toggleOrder(order.orderNumber)}>
                        <Td>
                          <div className="flex items-center gap-2 font-semibold text-slate-800 pl-2">
                            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOrderOpen ? "rotate-90" : ""}`} />
                            {order.orderNumber}
                          </div>
                        </Td>
                        <Td colSpan={2}>
                          <div className="text-sm text-slate-600">
                            <span className="font-medium text-slate-500">Order ETA:</span> {new Date(order.eta).toLocaleString("en-IN", {
                              day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true,
                            })}
                          </div>
                        </Td>
                        <Td colSpan={3}>
                          <div className="text-sm text-slate-600"><span className="font-medium text-slate-500">Time Remaining:</span> {order.remaining}</div>
                        </Td>
                        <Td></Td>
                        <Td><Pill tone="green">{order.status}</Pill></Td>
{/*                         <Td onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => onTrackOrder(order)} className="px-3 py-1 text-xs border rounded-md text-blue-600 border-blue-200 hover:bg-blue-50 w-full">Track</button>
                        </Td> */}
                        <Td onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          <button 
                            onClick={() => onTrackOrder(order)} 
                            className="px-3 py-1 text-xs border rounded-md text-blue-600 border-blue-200 hover:bg-blue-50"
                          >
                            Track
                          </button>
                          <button 
                            onClick={() => onNotifyOrder(order)} 
                            className="px-2.5 py-1 text-xs border rounded-md text-red-700 border-red-200 hover:text-white hover:bg-red-700 font-medium"
                            title="Notify Buyer via WhatsApp & In-App"
                          >
                            Notify
                          </button>
                        </div>
                      </Td>
                      </tr>
                      
                      {/* Expanded Order Content */}
                      {isOrderOpen && order.deliveryGroups.map(dg => {
                        const isDgOpen = expandedDgIds.includes(dg.dg);
                        const dgChecked = isDgFullySelected(dg, order);
                        const dgDelivered = dg.status?.toLowerCase() === "delivered";

                        return (
                          <React.Fragment key={dg.dg}>
                            {/* Delivery Group Row */}
                            <tr className="bg-white border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => toggleDg(dg.dg)}>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1 pl-[60px]">Delivery Group</div>
                                <div className="flex items-center gap-3 pl-8">
                                  {dgDelivered ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" title="Delivered" />
                                  ) : (
                                    <input type="checkbox" checked={dgChecked} onChange={(e) => handleSelectDg(dg, order, e.target.checked)} className="rounded border-slate-300 w-4 h-4 cursor-pointer shrink-0" onClick={(e) => e.stopPropagation()} />
                                  )}
                                  <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isDgOpen ? "rotate-90" : ""}`} />
                                  <span className="text-sm font-bold">{dg.dg}</span>
                                </div>
                              </Td>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">ETA</div>
                                <div className="text-xs text-slate-500">
                                {new Date(dg.eta).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}
                                </div>
                              </Td>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Remaining</div>
                                <div><Pill tone="amber">{dg.remaining}</Pill></div>
                              </Td>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Dist to Hub</div>
                                <div className="text-sm text-slate-700">{dg.distHub}</div>
                              </Td>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Dist to Cust</div>
                                <div className="text-sm text-slate-700">{dg.distCustomer}</div>
                              </Td>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Dist from Hub to Cust</div>
                                <div className="text-sm text-slate-700">{dg.distHubToCustomer}</div>
                              </Td>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Route</div>
                                <div className="text-sm font-medium text-slate-700">{dg.route}</div>
                              </Td>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Supplier(s)</div>
                                <div className="text-xs text-slate-500 whitespace-pre-wrap">{dg.suppliers}</div>
                              </Td>
                              <Td>
                                <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">Status</div>
                                <div><Pill tone="blue">{dg.status}</Pill></div>
                              </Td>
                              <Td onClick={(e) => e.stopPropagation()} className="align-bottom pb-3">
                                 <button onClick={() => onTrackDg(dg, order)} className="px-3 py-1 text-xs border rounded-md text-blue-600 border-blue-200 hover:bg-blue-50 w-full">Track</button>
                              </Td>
                            </tr>
                            
                            {/* Item Rows */}
                            {isDgOpen && dg.items.map(item => {
                              const Icon = CATEGORY_ICON[item.category] || Package;
                              const itemKey = getItemKey(order.orderNumber, dg.dg, item.po, item.id);
                              const isChecked = selectedItemKeys.has(itemKey);
                              const itemDelivered = item.status?.toLowerCase() === "delivered" || dg.status?.toLowerCase() === "delivered";
                              
                              return (
                                <tr 
                                  key={itemKey} 
                                  className={`bg-slate-50/50 border-b border-slate-100 ${!itemDelivered ? "hover:bg-slate-100 cursor-pointer" : "opacity-80"}`} 
                                  onClick={() => { if (!itemDelivered) handleSelectItem(itemKey, !isChecked); }}
                                >
                                  <Td colSpan={6}>
                                    <div className="flex items-center gap-4 pl-16 py-1">
                                      {itemDelivered ? (
                                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" title="Delivered" />
                                      ) : (
                                        <input type="checkbox" checked={isChecked} onChange={(e) => handleSelectItem(itemKey, e.target.checked)} className="rounded border-slate-300 w-4 h-4 cursor-pointer shrink-0" onClick={(e) => e.stopPropagation()} />
                                      )}
                                      
                                      <div className="flex items-center gap-3 w-56 shrink-0">
                                        <div className="group relative flex items-center shrink-0">
                                          <div className="w-8 h-8 rounded bg-white border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                                            {item?.image ? (
                                              <img src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${item.image}`} alt={item?.name || ""} className="w-full h-full object-cover" />
                                            ) : (
                                              <Icon className="w-4 h-4 text-slate-500" />
                                            )}
                                          </div>
                                        </div>
                                        <div className="text-sm font-medium text-slate-800 truncate" title={item.name}>{item.name}</div>
                                      </div>

                                      <div className="flex gap-5 text-xs text-slate-500 flex-1">
                                        <div><span className="font-medium text-slate-700">PO:</span> {item.po}</div>
                                        <div><span className="font-medium text-slate-700">Qty:</span> {`${item.qty} ${item.UOM || ''}`}</div>
                                        <div><span className="font-medium text-slate-700">Wt:</span> {typeof item.weight === 'object' ? `${item.weight.value} ${item.weight.unit}` : item.weight}</div>
                                        <div><span className="font-medium text-slate-700">Vol:</span> {item.volume}</div>
                                        <div><span className="font-medium text-slate-700">Dist (Hub):</span> {item.distHub} km</div>
                                        <div><span className="font-medium text-slate-700">Dist (Cust):</span> {item.distCustomer} km</div>
                                        <div><span className="font-medium text-slate-700">Dist (Hub to Cust):</span> {item.distHubToCustomer}</div>
                                        <div><span className="font-medium text-slate-700">Route:</span> {item.route} </div>
                                      </div>
                                    </div>
                                  </Td>
                                  <Td className="text-xs text-slate-500">{item.supplier}</Td>
                                  <Td><Pill tone={itemDelivered ? "green" : "slate"}>{item.status}</Pill></Td>
                                  <Td></Td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="border-t border-slate-200 px-4 pt-4 pb-4 bg-slate-50 rounded-b-xl shrink-0">
            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} itemsPerPage={5} onPageChange={handlePageChange} onPreviousPage={handlePreviousPage} onNextPage={handleNextPage} showInfo={true} itemName="Orders" />
          </div>
        )}
      </div>

      {showCreateShipment && <CreateShipmentModal initialItems={getSelectedItemsData()} onClose={() => setShowCreateShipment(false)} onRefresh={onRefresh} showPopup={showPopup}/>}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Screen 2 — Shipments                                                   */
/* ---------------------------------------------------------------------- */
function ShipmentsScreen({ shipments,loading,onTrack, onCreateTrip }) {
  const [expandedShipments, setExpandedShipments] = useState([]);
  const [selectedShipmentIds, setSelectedShipmentIds] = useState(new Set());
  
  // Add Pagination Hook
  const {
    currentItems: paginatedShipments,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
  } = usePagination(shipments || [], 5);

  const toggleShipment = (id) => {
    setExpandedShipments(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectShipment = (id, checked) => {
    const newSel = new Set(selectedShipmentIds);
    if (checked) newSel.add(id); else newSel.delete(id);
    setSelectedShipmentIds(newSel);
  };

  // Filter out delivered shipments so they aren't included in "Select All"
  const selectableShipments = shipments?.filter(s => s.status?.toLowerCase() !== "delivered") || [];

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedShipmentIds(new Set(selectableShipments.map(s => s._id || s.id)));
    } else {
      setSelectedShipmentIds(new Set());
    }
  };

  return (
    <div>
      <TopBar placeholder="Search shipments..." primaryLabel="Create Trip" onPrimary={() => {
         if (selectedShipmentIds.size > 0) onCreateTrip(selectedShipmentIds);
         else alert("Select shipments first");
      }} />
      {loading ? (
        <div className="text-center py-12 text-gray-500 border border-gray-100 rounded-lg bg-white shadow-sm w-full">
          Loading...
        </div>
      ) :(!shipments || shipments.length === 0) ? (<div className="flex flex-col items-center justify-center w-full py-16 text-gray-500 bg-white border border-gray-100 rounded-lg shadow-sm">
          <Package className="w-8 h-8 mb-2 text-gray-400" />
          <p className="text-sm font-medium">No Shipments Found</p>
        </div>
      ):(
            <div className="border border-slate-200 rounded-xl flex flex-col shadow-sm bg-white">
              <div className="overflow-auto max-h-[calc(100vh-380px)] min-h-[300px] flex-1">
                <table className="w-full border-collapse">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <Th className="w-12 text-center">
                        <input 
                          type="checkbox" 
                          onChange={(e) => handleSelectAll(e.target.checked)} 
                          checked={selectedShipmentIds.size === selectableShipments.length && selectableShipments.length > 0} 
                          disabled={selectableShipments.length === 0}
                          className="rounded border-slate-300 w-4 h-4 cursor-pointer disabled:opacity-50" 
                        />
                      </Th>
                      <Th className="w-10"></Th>
                      <Th>Shipment</Th>
                      <Th>Origin</Th>
                      <Th>Destination</Th>
                      <Th>ETA</Th>
                      <Th>Status</Th>
                      <Th>Track</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Render Paginated Shipments */}
                    {paginatedShipments?.map((s) => {
                      const currentId = s._id || s.id;
                      const isOpen = expandedShipments.includes(currentId);
                      const isSelected = selectedShipmentIds.has(currentId);
                      const isDelivered = s.status?.toLowerCase() === "delivered";

                      return (
                        <React.Fragment key={currentId}>
                          <tr className={`border-b border-slate-100 ${!isDelivered ? "hover:bg-slate-50 cursor-pointer" : "opacity-80"}`} onClick={() => toggleShipment(currentId)}>
                            <Td className="text-center" onClick={(e) => e.stopPropagation()}>
                              {isDelivered ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mx-auto" title="Delivered" />
                              ) : (
                                <input type="checkbox" checked={isSelected} onChange={(e) => handleSelectShipment(currentId, e.target.checked)} className="rounded border-slate-300 w-4 h-4 cursor-pointer" />
                              )}
                            </Td>
                            <Td><ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} /></Td>
                            <Td>
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                   {s?.image ? (
                                      <img src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${s?.image}`} alt={s?.shipment_number || ""} className="w-full h-full object-cover" />
                                    ) : (
                                      <PackageCheck className="w-4.5 h-4.5 text-slate-600" />
                                    )}
                                </div>
                                <div>
                                  <div className="font-medium text-slate-800">{s.shipment_number}</div>
                                  <div className="text-xs text-slate-400">{s.weight}</div>
                                </div>
                              </div>
                            </Td>
                            <Td>{s.origin}</Td>
                            <Td>{s.destination}</Td>
                            <Td>{s.eta}</Td>
                            <Td><Pill tone={isDelivered ? "green" : "blue"}>{s.status}</Pill></Td>
                            <Td onClick={(e) => e.stopPropagation()}>
                              <button onClick={() => onTrack(s)} className="px-3 py-1.5 text-xs font-medium rounded-md border text-blue-600 border-blue-200 hover:bg-blue-50">Track</button>
                            </Td>
                          </tr>
                          {isOpen &&
                            s.items.map((item) => {
                              const Icon = CATEGORY_ICON[item.category] || Package;

                              return (
                                <tr
                                  key={item.id}
                                  className="bg-slate-50/50 border-b border-slate-100"
                                >
                                  <Td></Td>
                                  <Td></Td>
                                  <Td colSpan={6}>
                                    <div className="flex items-center gap-6 pl-4">
                                      <div className="w-8 h-8 rounded bg-white border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                                        {item?.image ? (
                                          <img
                                            src={`${process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES}${item.image}`}
                                            alt={item?.name || ""}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <Icon className="w-4 h-4 text-slate-500" />
                                        )}
                                      </div>
                                      <div className="text-sm font-medium text-slate-800 w-64 truncate" title={item.name}>
                                        {item.name}
                                      </div>
                                      <div className="flex gap-8 text-xs text-slate-500">
                                        <div>
                                          <span className="font-medium text-slate-700">Qty:</span>{" "}
                                          {item.qty}
                                        </div>
                                        <div>
                                          <span className="font-medium text-slate-700">Wt:</span>{" "}
                                          {item.weight}
                                        </div>
                                        <div>
                                          <span className="font-medium text-slate-700">Dim:</span>{" "}
                                          {item.dimensions}
                                        </div>
                                        <div>
                                          <Pill tone="slate">{item.status}</Pill>
                                        </div>
                                      </div>
                                    </div>
                                  </Td>
                                </tr>
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="border-t border-slate-200 px-4 pt-4 pb-4 bg-slate-50 rounded-b-xl shrink-0">
                  <Pagination 
                    currentPage={currentPage} 
                    totalPages={totalPages} 
                    totalItems={totalItems} 
                    itemsPerPage={5} 
                    onPageChange={handlePageChange} 
                    onPreviousPage={handlePreviousPage} 
                    onNextPage={handleNextPage} 
                    showInfo={true} 
                    itemName="Shipments" 
                  />
                </div>
              )}
            </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Screen 3 — Trips                                                       */
/* ---------------------------------------------------------------------- */
/*function TripsScreen({ trips,onAssign, onTrack, onViewShipments, onViewRouteLegs }) {
  return (
    <div>
      {(!trips || trips.length === 0)?(
        <div className="flex flex-col items-center justify-center w-full py-16 text-gray-500 bg-white border border-gray-100 rounded-lg shadow-sm">
          <Route className="w-8 h-8 mb-2 text-gray-400" /> 
          <p className="text-sm font-medium">No Trips Found</p>
        </div>
      ):(
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <table className="w-full border-collapse">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>Trip ID</Th>
              <Th>Shipments</Th>
              <Th>Route Legs</Th>
              <Th>ETA</Th>
              <Th>Time Remaining</Th>
              <Th>Status</Th>
              <Th>Assign Partner</Th>
              <Th>Track</Th>
            </tr>
          </thead>
          <tbody>
            {trips.map((t) => {
              // Calculate total shipments across all legs
              const totalShipments = t.route_legs?.reduce((acc, leg) => acc + (leg.shipments?.length || 0), 0) || 0;
              const legCount = t.route_legs?.length || 0;

              return (
              <tr key={t._id} className="border-b border-slate-100 hover:bg-slate-50">
                <Td className="text-xs font-medium">{t.trip_number}</Td>
                <Td>
                  <button onClick={() => onViewShipments(t)} className="text-blue-600 hover:underline font-medium">
                     {totalShipments} shipments
                  </button>
                </Td>
                <Td>
                   <button onClick={() => onViewRouteLegs(t)} className="text-blue-600 hover:underline font-medium">
                     {legCount} Legs
                   </button>
                </Td>
                <Td>{t.estimated_delivery_time || 'N/A'}</Td>
                <Td><Pill tone="amber">{t.isDelayed ? "Delayed" : "On Time"}</Pill></Td>
                <Td><Pill tone={t.status === "in_transit" ? "blue" : "slate"}>{t.status}</Pill></Td>
                <Td>
                  {t.partner_id ? (
                     <div className="flex items-center gap-3">
                        <div>
                          <div className="text-sm font-medium text-slate-800">{t.partner_id.name}</div>
                          <div className="text-xs text-slate-500">{t.partner_id.vehicle_type}</div>
                          <div className="text-xs text-slate-500">{t.partner_id.mobile}</div>
                        </div>
                        <button onClick={() => onAssign(t, true)} className="p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded border border-transparent hover:border-slate-200">
                           <Edit2 className="w-3.5 h-3.5" />
                        </button>
                     </div>
                  ) : (
                    <button onClick={() => onAssign(t, false)} className="px-3 py-1.5 text-xs font-medium rounded-md text-white" style={{ backgroundColor: ACCENT }}>Assign</button>
                  )}
                </Td>
                <Td>
                  <button onClick={() => onTrack(t)} className="px-3 py-1.5 text-xs font-medium rounded-md border text-blue-600 border-blue-200 hover:bg-blue-50">Track</button>
                </Td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}*/

// Add onPhoneClick to your props
function TripsScreen({ trips, loading, onAssign, onTrack, onViewShipments, onViewRouteLegs, onPhoneClick }) {
  
  // Add Pagination Hook
  const {
    currentItems: paginatedTrips,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
  } = usePagination(trips || [], 5);

  return (
    <div>
      {loading ? (
        <div className="text-center py-12 text-gray-500 border border-gray-100 rounded-lg bg-white shadow-sm w-full">
          Loading...
        </div>
      ) :(!trips || trips.length === 0)?(
        <div className="flex flex-col items-center justify-center w-full py-16 text-gray-500 bg-white border border-gray-100 rounded-lg shadow-sm">
          <Route className="w-8 h-8 mb-2 text-gray-400" />
          <p className="text-sm font-medium">No Trips Found</p>
        </div>
      ):(
      <div className="border border-slate-200 rounded-xl flex flex-col shadow-sm bg-white">
        <div className="overflow-auto max-h-[calc(100vh-380px)] min-h-[300px] flex-1">
          <table className="w-full border-collapse">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Trip ID</Th>
                <Th>Shipments</Th>
                <Th>Route Legs</Th>
                <Th>ETA</Th>
                <Th>Time Remaining</Th>
                <Th>Status</Th>
                <Th>Assign Partner</Th>
                <Th>Track</Th>
              </tr>
            </thead>
            <tbody>
              {/* Render Paginated Trips */}
              {paginatedTrips?.map((t) => {
                const totalShipments = t.route_legs?.reduce((acc, leg) => acc + (leg.shipments?.length || 0), 0) || 0;
                const legCount = t.route_legs?.length || 0;

                return (
                <tr key={t._id} className="border-b border-slate-100 hover:bg-slate-50">
                  <Td className="text-xs font-medium">{t.trip_number}</Td>
                  <Td>
                    <button onClick={() => onViewShipments(t)} className="text-blue-600 hover:underline font-medium">
                       {totalShipments} shipments
                    </button>
                  </Td>
                  <Td>
                     <button onClick={() => onViewRouteLegs(t)} className="text-blue-600 hover:underline font-medium">
                       {legCount} Legs
                     </button>
                  </Td>
                  <Td>{t.estimated_delivery_time || 'N/A'}</Td>
                  <Td><Pill tone="amber">{t.isDelayed ? "Delayed" : "On Time"}</Pill></Td>
                  <Td><Pill tone={t.status === "in_transit" ? "blue" : "slate"}>{t.status}</Pill></Td>
                  <Td>
                    {t.partner_id ? (
                       <div className="flex items-center gap-3">
                          <div>
                            <div className="text-sm font-medium text-slate-800">{t.partner_id.name}</div>
                            <div className="text-xs text-slate-500 mb-0.5">{t.partner_id.vehicle_type}</div>
                            <button 
                              onClick={(e) => { e.stopPropagation(); onPhoneClick(t.partner_id.mobile); }}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1 transition-colors"
                            >
                              <Phone className="w-3 h-3" /> {t.partner_id.mobile}
                            </button>
                          </div>
                          <button onClick={() => onAssign(t, true)} className="p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600 rounded border border-transparent hover:border-slate-200">
                             <Edit2 className="w-3.5 h-3.5" />
                          </button>
                       </div>
                    ) : (
                      <button onClick={() => onAssign(t, false)} className="px-3 py-1.5 text-xs font-medium rounded-md text-white" style={{ backgroundColor: ACCENT }}>Assign</button>
                    )}
                  </Td>
                  <Td>
                    <button onClick={() => onTrack(t)} className="px-3 py-1.5 text-xs font-medium rounded-md border text-blue-600 border-blue-200 hover:bg-blue-50">Track</button>
                  </Td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="border-t border-slate-200 px-4 pt-4 pb-4 bg-slate-50 rounded-b-xl shrink-0">
            <Pagination 
              currentPage={currentPage} 
              totalPages={totalPages} 
              totalItems={totalItems} 
              itemsPerPage={5} 
              onPageChange={handlePageChange} 
              onPreviousPage={handlePreviousPage} 
              onNextPage={handleNextPage} 
              showInfo={true} 
              itemName="Trips" 
            />
          </div>
        )}
      </div>
      )}
    </div>
  );
}

const FLEET_STEP_OPTIONS = ["Brickbucket", "3 PL", "On Demand", "Supplier"];
const VEHICLE_STEP_OPTIONS = [
  { label: "Bike", icon: Bike },
  { label: "Passenger Auto", icon: Car },
  { label: "Truck Auto", icon: Truck },
  { label: "Truck Van", icon: Truck },
];

/*function TripWizard({ mode, partners, trip, onClose, onRefresh }) {
  const [step, setStep] = useState(1);
  const [deliveryType, setDeliveryType] = useState("Direct");
  const [priority, setPriority] = useState("Medium");
  const [notes, setNotes] = useState("");
  const [fleet, setFleet] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [partner, setPartner] = useState(trip?.partner_id || null);

  const isAssign = mode === "assign";
  const allSteps = isAssign 
     ? ["Select Fleet", "Vehicle Type", "Select Partner"] 
     : ["Type & Priority", "Select Fleet", "Vehicle Type", "Select Partner"];
     
  const title = isAssign ? "Assign / Edit Fleet & Partner" : "Create Trip";
  const currentStepLabel = allSteps[step - 1];
  const canProceed = 
      (currentStepLabel === "Type & Priority") || 
      (currentStepLabel === "Select Fleet" && fleet) ||
      (currentStepLabel === "Vehicle Type" && vehicle) ||
      (currentStepLabel === "Select Partner" && partner);

  const createOrUpdateTrip = async () => {
      try {
        const payload = {
          type: deliveryType,
          priority: priority,
          notes: notes,
          partner_id: partner?._id || undefined,
          status: partner ? "assigned" : "draft"
        };

        if (mode === "assign" && trip?._id) {
          payload.trip_id = trip._id;
        }

        const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-trip`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'x-token': decryptData(sessionStorage.getItem("token"))
          },
          body: JSON.stringify(payload)
        });
        
        const result = await response.json();
        if(result.success) onClose();
      } catch(err) {
        console.error(err);
      }
    };

const editTripAssignment = async () => {
    try {
      const payload = {
        trip_id: trip._id,
        partner_id: partner?._id || undefined,
        // vehicle_id: vehicle?._id // If tracking vehicle ObjectIds separately
      };

      const token = decryptData(sessionStorage.getItem("token"));
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/edit-trip-assignment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-token': token || ""
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      if (result.success || response.ok) {
        if (onRefresh) onRefresh();
        onClose();
      } else {
        alert(result.message || "Failed to assign partner");
      }
    } catch (error) {
      console.error("Assignment error:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl border border-slate-200 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="text-sm font-semibold text-slate-800">{title}</div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="flex items-center gap-2 px-6 pt-4">
          {allSteps.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium shrink-0" style={{ backgroundColor: step > i + 1 ? SUCCESS : step === i + 1 ? ACCENT : "#e2e8f0", color: step >= i + 1 ? "#fff" : "#64748b" }}>
                {step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <span className={`text-xs ${step === i + 1 ? "text-slate-700 font-medium" : "text-slate-400"}`}>{label}</span>
              {i < allSteps.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
            </div>
          ))}
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {currentStepLabel === "Type & Priority" && (
            <div className="space-y-5">
              <div>
                <div className="text-xs font-medium text-slate-500 mb-2">Delivery Type</div>
                <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
                  {["Direct", "Hub & Spoke"].map((opt) => (
                    <button key={opt} onClick={() => setDeliveryType(opt)} className={`px-4 py-1.5 text-sm rounded-md transition-colors ${deliveryType === opt ? "bg-white text-slate-800 shadow-sm border border-slate-200" : "text-slate-500"}`}>{opt}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-500 mb-2">Priority</div>
                <div className="flex gap-2">
                  {["High", "Medium", "Low"].map((opt) => (
                    <button key={opt} onClick={() => setPriority(opt)} className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${priority === opt ? "text-white border-transparent" : "text-slate-500 border-slate-200 hover:bg-slate-50"}`} style={priority === opt ? { backgroundColor: ACCENT } : {}}>{opt}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-500 mb-2">Notes / Special Instructions</div>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none" />
              </div>
            </div>
          )}

          {currentStepLabel === "Select Fleet" && (
            <div>
              <div className="text-xs font-medium text-slate-500 mb-3">Select Fleet</div>
              <div className="grid grid-cols-2 gap-3">
                {FLEET_STEP_OPTIONS.map((f) => (
                  <button key={f} onClick={() => setFleet(f)} className={`text-left border rounded-lg px-4 py-3 text-sm transition-colors ${fleet === f ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>{f}</button>
                ))}
              </div>
            </div>
          )}

          {currentStepLabel === "Vehicle Type" && (
            <div>
              <div className="text-xs font-medium text-slate-500 mb-3">Select Vehicle Type</div>
              <div className="grid grid-cols-2 gap-3">
                {VEHICLE_STEP_OPTIONS.map((v) => (
                  <button key={v.label} onClick={() => setVehicle(v.label)} className={`flex items-center gap-3 border rounded-lg px-4 py-3 text-sm transition-colors ${vehicle === v.label ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                    <v.icon className="w-4 h-4" /> {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStepLabel === "Select Partner" && (
                <div>
                  <div className="text-xs font-medium text-slate-500 mb-3">Select Partner / Driver</div>
                  <div className="grid grid-cols-2 gap-3">
                    {partners.map((p) => (
                      <button key={p._id} onClick={() => setPartner(p)} className={`text-left border rounded-lg px-4 py-3 transition-colors ${partner?._id === p._id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                        <div className="flex items-center gap-2 mb-1.5"><Truck className="w-3.5 h-3.5 text-slate-500" /><span className="text-sm font-medium text-slate-800">{p.name}</span></div>
                        <div className="text-xs text-slate-500 flex items-center gap-1.5 mb-0.5"><Phone className="w-3 h-3" /> {p.mobile}</div>
                        <div className="text-xs text-slate-400 font-mono">{p.vehicle_number}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
          <button onClick={() => (step === 1 ? onClose() : setStep(step - 1))} className="flex items-center gap-1 px-4 py-2 text-sm text-slate-600 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /> {step === 1 ? "Cancel" : "Back"}</button>
          <button 
                disabled={!canProceed} 
                onClick={() => {
                  if (step === allSteps.length) {
                    isAssign ? editTripAssignment() : createOrUpdateTrip();
                  } else {
                    setStep(step + 1);
                  }
                }} 
                className="flex items-center gap-1 px-5 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-40" 
                style={{ backgroundColor: ACCENT }}
              >
                {step === allSteps.length ? (isAssign ? "Confirm Assignment" : "Create Trip") : "Next"}
                {step < allSteps.length && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}*/

function TripWizard({ mode, partners: initialPartners = [], trip, onClose, onRefresh,showPopup }) {
  const [step, setStep] = useState(1);
  const [deliveryType, setDeliveryType] = useState("Direct");
  const [priority, setPriority] = useState("Medium");
  const [notes, setNotes] = useState("");
  const [fleet, setFleet] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [partner, setPartner] = useState(trip?.partner_id || null);
  const [partnersList, setPartnersList] = useState(initialPartners);
  const [loadingPartners, setLoadingPartners] = useState(false);

  // State for Fare
  const [fare, setFare] = useState(trip?.total_value || "");

  // New states for 3PL integration
  const [thirdPartyApp, setThirdPartyApp] = useState(null);
  const [waNumber, setWaNumber] = useState("");

  const isAssign = mode === "assign";
  // Added "Enter Fare" to the end of the steps
  const allSteps = isAssign 
      ? ["Select Fleet", "Vehicle Type", "Select Partner", "Enter Fare"] 
      : ["Type & Priority", "Select Fleet", "Vehicle Type", "Select Partner", "Enter Fare"];
      
  const title = isAssign ? "Assign / Edit Fleet & Partner" : "Create Trip";
  const currentStepLabel = allSteps[step - 1];
  
  // Added validation for the new Fare step
  const canProceed = 
      (currentStepLabel === "Type & Priority") || 
      (currentStepLabel === "Select Fleet" && fleet) ||
      (currentStepLabel === "Vehicle Type" && vehicle) ||
      (currentStepLabel === "Select Partner" && (partner || thirdPartyApp)) ||
      (currentStepLabel === "Enter Fare" && fare !== "");

  // Extract pickup and dropoff data for the WhatsApp share feature
  const getTripExtents = () => {
    if (!trip || !trip.route_legs || trip.route_legs.length === 0) return { pickup: "Pending", dropoff: "Pending" };
    const firstLeg = trip.route_legs[0];
    const lastLeg = trip.route_legs[trip.route_legs.length - 1];
    const pickup = firstLeg.stops?.[0]?.location?.address || firstLeg.origin?.name || "Origin";
    const dropoff = lastLeg.stops?.[lastLeg.stops.length - 1]?.location?.address || lastLeg.destination?.name || "Destination";
    return { pickup, dropoff };
  };

  const { pickup, dropoff } = getTripExtents();
  const waText = `*New Trip Booking*\nTrip ID: ${trip?.trip_number || 'TBD'}\n\n*Pickup:* ${pickup}\n*Dropoff:* ${dropoff}\n\nDistance: ${trip?.totalDistance ? trip.totalDistance + ' km' : 0}`;

  useEffect(() => {
    if (!initialPartners || initialPartners.length === 0) {
      const fetchPartnerList = async () => {
        try {
          setLoadingPartners(true);
          const token = decryptData(sessionStorage.getItem("token"));
          const response = await fetch(
            `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery-partner/get-all-delivery-partners`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "x-token": token || "",
              },
            }
          );
          const data = await response.json();
          if (data.success && data.data?.partners) {
            setPartnersList(data.data.partners);
          }
        } catch (err) {
          console.error("Error fetching partners in TripWizard:", err);
        } finally {
          setLoadingPartners(false);
        }
      };
      fetchPartnerList();
    } else {
      setPartnersList(initialPartners);
    }
  }, [initialPartners]);

  const filteredPartners = partnersList.filter((p) => {
    // Fleet Match
    let matchesFleet = true;

    if (fleet) {
      // Check multiple potential properties and default to "Brickbucket" if missing
      let rawFleet = p.fleet || p.fleetType || p.type || "Brickbucket";
      
      // Normalize legacy mock data ("own") to match "Brickbucket"
      if (rawFleet.toLowerCase() === "own") {
        rawFleet = "Brickbucket";
      }

      const partnerFleet = rawFleet.trim().toLowerCase();
      const selectedFleet = fleet.trim().toLowerCase();

      matchesFleet = partnerFleet === selectedFleet;
    }

    // Vehicle Match
    let matchesVehicle = true;

    if (vehicle) {
      const partnerVehicle = (
        p.vehicle_type ||
        p.vehicleType ||
        ""
      ).trim().toLowerCase();

      const selectedVehicle = vehicle.trim().toLowerCase();

      matchesVehicle = partnerVehicle === selectedVehicle;
    }

    // Both filters must match
    return matchesFleet && matchesVehicle;
  });

  const createOrUpdateTrip = async () => {
      try {
        const payload = {
          type: deliveryType,
          priority: priority,
          notes: notes,
          partner_id: partner?._id || undefined,
          status: partner ? "assigned" : (thirdPartyApp ? "3pl_assigned" : "draft"),
          platform: thirdPartyApp || "brickbucket",
          total_value: fare
        };

        if (mode === "assign" && trip?._id) {
          payload.trip_id = trip._id;
        }

        const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/create-or-update-trip`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'x-token': decryptData(sessionStorage.getItem("token"))
          },
          body: JSON.stringify(payload)
        });
        
        const result = await response.json();
          if (result.success) {
          if (onRefresh) onRefresh();
          showPopup("Trip created successfully!", "success", "trips");
          onClose();
        } else {
          showPopup(result.message || "Failed to create trip", "error");
        } 
      } catch(err) {
        console.error(err);
        showPopup("An error occurred while saving the trip", "error");
      }
    };

  const editTripAssignment = async () => {
    try {
      const payload = {
        trip_id: trip._id,
        partner_id: partner?._id || undefined,
        platform: thirdPartyApp || undefined,
        total_value: fare
      };

      console.log(payload)

      const token = decryptData(sessionStorage.getItem("token"));
      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/edit-trip-assignment`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-token': token || ""
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      if (result.success || response.ok) {
        if (onRefresh) onRefresh();
        showPopup("Partner assigned successfully!", "success");
        onClose();
      } else {
        showPopup(result.message || "Failed to assign partner", "error");
      }
    } catch (error) {
      console.error("Assignment error:", error);
      showPopup("An error occurred during assignment", "error");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl border border-slate-200 max-h-[92vh] flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="text-sm font-semibold text-slate-800">{title}</div>
            
            {/* Added: Display Total Distance if available */}
            {trip?.totalDistance && (
              <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-full border border-slate-200 flex items-center gap-1.5">
                 <Route className="w-3 h-3 text-slate-400" />
                 {trip.totalDistance} km
              </span>
            )}
            {trip?.total_weight && (
              <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2.5 py-0.5 rounded-full border border-slate-200 flex items-center gap-1.5">
                 <Package className="w-3 h-3 text-slate-400" />
                 {trip.total_weight} kg
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100">
             <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-6 pt-4">
          {allSteps.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-medium shrink-0" style={{ backgroundColor: step > i + 1 ? SUCCESS : step === i + 1 ? ACCENT : "#e2e8f0", color: step >= i + 1 ? "#fff" : "#64748b" }}>
                {step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}
              </div>
              <span className={`text-xs ${step === i + 1 ? "text-slate-700 font-medium" : "text-slate-400"}`}>{label}</span>
              {i < allSteps.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
            </div>
          ))}
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {currentStepLabel === "Type & Priority" && (
            <div className="space-y-5">
              <div>
                <div className="text-xs font-medium text-slate-500 mb-2">Delivery Type</div>
                <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50">
                  {["Direct", "Hub & Spoke"].map((opt) => (
                    <button key={opt} onClick={() => setDeliveryType(opt)} className={`px-4 py-1.5 text-sm rounded-md transition-colors ${deliveryType === opt ? "bg-white text-slate-800 shadow-sm border border-slate-200" : "text-slate-500"}`}>{opt}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-500 mb-2">Priority</div>
                <div className="flex gap-2">
                  {["High", "Medium", "Low"].map((opt) => (
                    <button key={opt} onClick={() => setPriority(opt)} className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${priority === opt ? "text-white border-transparent" : "text-slate-500 border-slate-200 hover:bg-slate-50"}`} style={priority === opt ? { backgroundColor: ACCENT } : {}}>{opt}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-slate-500 mb-2">Notes / Special Instructions</div>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 resize-none" />
              </div>
            </div>
          )}

          {currentStepLabel === "Select Fleet" && (
            <div>
              <div className="text-xs font-medium text-slate-500 mb-3">Select Fleet</div>
              <div className="grid grid-cols-2 gap-3">
                {FLEET_STEP_OPTIONS.map((f) => (
                  <button key={f} onClick={() => setFleet(f)} className={`text-left border rounded-lg px-4 py-3 text-sm transition-colors ${fleet === f ? "border-blue-400 bg-blue-50 text-blue-700 font-bold" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>{f}</button>
                ))}
              </div>
            </div>
          )}

          {currentStepLabel === "Vehicle Type" && (
            <div>
              <div className="text-xs font-medium text-slate-500 mb-3">Select Vehicle Type</div>
              <div className="grid grid-cols-2 gap-3">
                {VEHICLE_STEP_OPTIONS.map((v) => (
                  <button key={v.label} onClick={() => setVehicle(v.label)} className={`flex items-center gap-3 border rounded-lg px-4 py-3 text-sm transition-colors ${vehicle === v.label ? "border-blue-400 bg-blue-50 text-blue-700 font-bold" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                    <v.icon className="w-4 h-4" /> {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {currentStepLabel === "Select Partner" && fleet !== "3 PL" && (
            <div>
              <div className="text-xs font-medium text-slate-500 mb-3">Select Partner / Driver</div>
              <div className="grid grid-cols-2 gap-3">
                {filteredPartners.length > 0 ? (
                  filteredPartners.map((p) => (
                    <button key={p._id || p.name} onClick={() => setPartner(p)} className={`text-left border rounded-lg px-4 py-3 transition-colors ${partner?._id === p._id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <div className="flex items-center gap-2 mb-1.5"><Truck className="w-3.5 h-3.5 text-slate-500" /><span className="text-sm font-medium text-slate-800">{p.name}</span></div>
                      <div className="text-xs text-slate-500 flex items-center gap-1.5 mb-0.5"><Phone className="w-3 h-3" /> {p.mobile || p.phone}</div>
                      <div className="text-xs text-slate-400 font-mono">{p.vehicle_number || p.reg}</div>
                    </button>
                  ))
                ) : (
                  <div className="col-span-2 p-4 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50 text-slate-400 text-xs">
                    No partners found for the selected fleet.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* New 3PL Booking & WhatsApp Interface */}
          {currentStepLabel === "Select Partner" && fleet === "3 PL" && (
            <div>
              <div className="text-xs font-medium text-slate-500 mb-3">Select 3PL Booking Platform</div>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {["Ola", "Uber", "Rapido", "Porter", "Shadowfax", "Dunzo"].map((app) => (
                    <button key={app} onClick={() => setThirdPartyApp(app)} className={`text-left border rounded-lg px-4 py-3 transition-colors ${thirdPartyApp === app ? "border-blue-400 bg-blue-50 text-blue-700 font-bold" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                        {app}
                    </button>
                ))}
              </div>
              
              <div className="border-t border-slate-200 pt-5">
                 <div className="text-xs font-medium text-slate-500 mb-2">Share Trip Data to Booking Agent / Driver via WhatsApp</div>
                 <div className="flex gap-2 mb-3">
                    <input 
                      type="text" 
                      placeholder="Enter mobile number" 
                      value={waNumber} 
                      onChange={(e) => setWaNumber(e.target.value)} 
                      className="flex-1 text-sm rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-blue-400" 
                    />
                    <button 
                      onClick={() => window.open(`https://wa.me/${waNumber.replace(/\D/g,'')}?text=${encodeURIComponent(waText)}`, '_blank')} 
                      className="px-4 py-2 bg-[#25D366] text-white text-sm font-medium rounded-md hover:bg-[#20b958] transition-colors"
                    >
                      Share
                    </button>
                 </div>
                 <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 whitespace-pre-wrap font-mono">
                    <span className="font-semibold block mb-1 text-slate-500 font-sans">Message Preview:</span>
                    {waText}
                 </div>
              </div>
            </div>
          )}

          {/* Fare Input Step */}
          {currentStepLabel === "Enter Fare" && (
            <div>
              <div className="text-xs font-medium text-slate-500 mb-3">Enter Trip Fare</div>
              <div>
                <input 
                  type="number" 
                  placeholder="Enter total fare value" 
                  value={fare} 
                  onChange={(e) => setFare(e.target.value)} 
                  className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400" 
                />
              </div>
            </div>
          )}

        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
          <button onClick={() => (step === 1 ? onClose() : setStep(step - 1))} className="flex items-center gap-1 px-4 py-2 text-sm text-slate-600 rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /> {step === 1 ? "Cancel" : "Back"}</button>
          <button 
                disabled={!canProceed} 
                onClick={() => {
                  if (step === allSteps.length) {
                    isAssign ? editTripAssignment() : createOrUpdateTrip();
                  } else {
                    setStep(step + 1);
                  }
                }} 
                className="flex items-center gap-1 px-5 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-40" 
                style={{ backgroundColor: ACCENT }}
              >
                {step === allSteps.length ? (isAssign ? "Confirm Assignment" : "Create Trip") : "Next"}
                {step < allSteps.length && <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Screen 4 — Fleet & Partners                                            */
/* ---------------------------------------------------------------------- */
/* function FleetScreen() {
  const [page, setPage] = useState(1);
  const pageSize = 5;
  const totalPages = Math.ceil(PARTNERS.length / pageSize);
  const pagePartners = PARTNERS.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div>
      <div className="text-sm font-medium text-slate-700 mb-3">Delivery Fleets & 3PL</div>
      <div className="grid grid-cols-2 gap-4 mb-8">
        {FLEETS.map((f) => (
          <div key={f.name} className="border border-slate-200 rounded-xl p-5 bg-white flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">{f.name}</div>
              <div className="text-xs text-slate-400 mb-3">{f.subtitle}</div>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span>{f.vehicles} vehicles</span>
                <span>{f.drivers} drivers</span>
              </div>
            </div>
            <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: ACCENT_SOFT }}>
              <f.icon className="w-5 h-5" style={{ color: ACCENT }} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-medium text-slate-700">Registered Partners & Drivers</div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by name, phone, or vehicle..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
          />
        </div>
        <div className="flex-1" />
        <button
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg"
          style={{ backgroundColor: ACCENT }}
        >
          <Plus className="w-4 h-4" /> Add Partner
        </button>
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        {pagePartners.map((p, i) => (
          <div
            key={p.name}
            className={`flex items-center gap-4 px-4 py-3.5 ${i !== pagePartners.length - 1 ? "border-b border-slate-100" : ""}`}
          >
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-slate-500" />
            </div>
            <div className="w-40 shrink-0">
              <div className="text-sm font-medium text-slate-800">{p.name}</div>
            </div>
            <Pill tone="blue">{p.vehicleType}</Pill>
            <Pill tone="slate">{p.fleetType}</Pill>
            <div className="flex items-center gap-1.5 text-xs text-slate-500 w-36 shrink-0">
              <Phone className="w-3 h-3" /> {p.phone}
            </div>
            <div className="text-xs font-mono text-slate-500 w-28 shrink-0">{p.reg}</div>
            <div className="text-xs text-slate-400 flex-1 truncate">{p.email}</div>
            <button className="p-1.5 rounded-md hover:bg-slate-100 shrink-0">
              <Edit2 className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-xs text-slate-400">
          Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, PARTNERS.length)} of {PARTNERS.length}
        </div>
        <div className="flex items-center gap-1">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="p-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
          >
            <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              onClick={() => setPage(n)}
              className={`w-7 h-7 text-xs rounded-md ${n === page ? "text-white" : "text-slate-500 hover:bg-slate-50 border border-slate-200"}`}
              style={n === page ? { backgroundColor: ACCENT } : {}}
            >
              {n}
            </button>
          ))}
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="p-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
          >
            <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
          </button>
        </div>
      </div>
    </div>
  );
} */

function FleetScreen({ fleets = [], partners = [], vehicles = [], loading, onRefresh,showPopup }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFleet, setSelectedFleet] = useState(null);
  
  // Modal, Edit & Popup State
  const [showAddPartnerPopup, setShowAddPartnerPopup] = useState(false);
  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [partnerForm, setPartnerForm] = useState({
    name: "",
    mobile: "",
    email: "",
    vehicle_number: "",
    vehicle_type: "",
    platform: "brickbucket",
    fleet: "brickbucket"
  });

  // Filter partners by Search and Selected Fleet
  const filteredPartners = partners.filter((p) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
      p.name?.toLowerCase().includes(searchLower) ||
      p.mobile?.includes(searchQuery) ||
      p.vehicle_number?.toLowerCase().includes(searchLower) ||
      p.email?.toLowerCase().includes(searchLower);
    
    const partnerFleet = p.fleet || p.type || "Brickbucket"; 
    const matchesFleet = selectedFleet ? partnerFleet.toLowerCase() === selectedFleet.toLowerCase() : true;
    
    return matchesSearch && matchesFleet;
  });

  // Pagination hook (5 per page)
  const {
    currentItems: paginatedPartners,
    currentPage,
    totalPages,
    totalItems,
    handlePageChange,
    handlePreviousPage,
    handleNextPage,
  } = usePagination(filteredPartners, 3);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    handlePageChange(1);
  };

  const toggleFleetFilter = (fleet) => {
    setSelectedFleet(prev => (prev === fleet ? null : fleet));
    handlePageChange(1);
  };

  const resetForm = () => {
    setPartnerForm({ name: "", mobile: "", email: "", vehicle_number: "", vehicle_type: "", platform: "brickbucket", fleet: "brickbucket" });
    setEditingPartnerId(null);
  };

  const handleEditClick = (partner) => {
    setPartnerForm({
      name: partner.name || "",
      mobile: partner.mobile || "",
      email: partner.email || "",
      vehicle_number: partner.vehicle_number || "",
      vehicle_type: partner.vehicle_type || "",
      platform: partner.platform || "brickbucket",
      fleet: partner.fleet || "brickbucket"
    });
    setEditingPartnerId(partner._id || partner.id);
    setShowAddPartnerPopup(true);
  };

  // API Submission for Create & Update
  const handleSavePartner = async () => {
    try {
      setIsSubmitting(true);
      if (!partnerForm.name || !partnerForm.mobile || !partnerForm.vehicle_number || !partnerForm.vehicle_type) {
        showPopup("Please fill all required fields", "error");
        return;
      }

      const encryptedToken = sessionStorage.getItem('token');
      const token = decryptData(encryptedToken);
      const encryptedhubId = sessionStorage.getItem('hubId');
      const hubId = decryptData(encryptedhubId);

      const payload = {
        ...partnerForm,
        hubId: hubId,
        roleId: 15,
      };

      if (editingPartnerId) {
        payload.partner_id = editingPartnerId; 
      }

      const endpoint = editingPartnerId 
        ? "update-delivery-partner" 
        : "create-delivery-partner";

      const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery-partner/${endpoint}`, {
        method: editingPartnerId ? "PUT" : "POST", 
        headers: { "Content-Type": "application/json", "x-token": token || "" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (result.success || response.ok) {
        showPopup(`Delivery partner ${editingPartnerId ? "updated" : "created"} successfully!`, "success");
        setShowAddPartnerPopup(false);
        resetForm();
        if (onRefresh) onRefresh();
      } else {
        showPopup(result.message || "Failed to save partner", "error");
      }
    } catch (error) {
      showPopup("Network error. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getVehicleIcon = (type) => {
    if (!type) return "🚗";
    const key = type.toLowerCase();
    return vehicleTypeIcon[key] || "🚗";
  };

  return (
    <div className="space-y-6 relative">
      {/* ── Custom Status Popup Overlay ── */}
{/*       {popup.show && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-sm w-full text-center animate-in zoom-in duration-200">
            {popup.type === 'success' ? (
              <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
            ) : (
              <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            )}
            <h3 className="font-bold text-xl text-gray-900 mb-2">
              {popup.type === 'success' ? 'Success!' : 'Error'}
            </h3>
            <p className="text-gray-600 text-sm mb-6">{popup.message}</p>
            <Button 
              className={`w-full ${popup.type === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`} 
              onClick={() => setPopup({ show: false, message: "", type: "success" })}
            >
              Close
            </Button>
          </div>
        </div>
      )} */}

{/* ── Fleet Categories ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Delivery Fleets & 3PL</h4>
        </div>
        {loading ? (
          <div className="text-center py-12 text-gray-500 border rounded-lg">Loading...</div>
        ) :(!fleets || fleets.length === 0)?(
          <div className="flex flex-col items-center justify-center py-10 text-sm text-gray-500 border rounded-lg">
            <Truck className="w-8 h-8 mb-2 text-gray-400" />
            <p className="font-medium">No fleets found.</p>
          </div>
        ):(
        <div className="grid grid-cols-2 gap-3">
          {fleets.map((f) => {
            const isSelected = selectedFleet === f;
            
            // Restore the dynamic icon and color mapping
            const typeCfg = partnerTypeConfig[f] || { label: f, color: "bg-gray-100 text-gray-700", icon: Truck };
            const TypeIcon = typeCfg.icon;

            return (
              <Card 
                key={f} 
                className={`p-4 cursor-pointer transition-all ${isSelected ? "ring-2 ring-blue-500 bg-blue-50/20" : "hover:border-gray-300"}`}
                onClick={() => toggleFleetFilter(f)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${typeCfg.color.replace("text-", "bg-").replace("-700", "-100")}`}>
                      <TypeIcon className={`w-4 h-4 ${typeCfg.color.split(" ")[1]}`} />
                    </div>
                    <div>
                      <p className={`font-medium text-sm ${isSelected ? "text-blue-700" : ""}`}>{f}</p>
                      {/* Restored the dynamic label badge */}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${typeCfg.color}`}>
                        {typeCfg.label}
                      </span>
                    </div>
                  </div>
                  {isSelected && <CheckCircle className="w-4 h-4 text-blue-500" />}
                </div>
              </Card>
            );
          })}
        </div>
        )}
      </div>

      {/* ── Partners List & Search ── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Registered Partners & Drivers</h4>
          <Button size="sm" onClick={() => { resetForm(); setShowAddPartnerPopup(true); }} className="bg-[#f7525a] text-white hover:bg-[#f7525a]">
            <Plus className="w-4 h-4 mr-1" /> Add Partner
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              className="pl-9 bg-white" 
              placeholder="Search by name, phone, or vehicle..." 
              value={searchQuery} 
              onChange={handleSearchChange} 
            />
          </div>
          {selectedFleet && (
            <Badge variant="secondary" className="flex items-center gap-1 bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer" onClick={() => toggleFleetFilter(selectedFleet)}>
              Filtered by: {selectedFleet} <X className="w-3 h-3 ml-1" />
            </Badge>
          )}
        </div>

        <div className="space-y-2 ">
          {loading ? (
            <div className="text-center py-12 text-gray-500 border rounded-lg bg-white">Loading...</div>
          ) :paginatedPartners.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-sm text-gray-500 border rounded-lg">
              <Users className="w-8 h-8 mb-2 text-gray-400" />
              <p className="font-medium">No delivery partners found.</p>
            </div>
          ) : (
            paginatedPartners.map((p) => (
              <div key={p._id || p.id} className="flex items-center justify-between gap-4 p-3 border rounded-lg bg-card hover:bg-accent/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{p.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-mono uppercase flex items-center gap-1">
                      {getVehicleIcon(p.vehicle_type)} {p.vehicle_type || "Vehicle"}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border">
                      {p.platform || p.fleet || "Internal"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Phone className="w-3 h-3"/> {p.mobile}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1 font-mono">{p.vehicle_number || "N/A"}</span>
                    {p.email && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-1">✉️ {p.email}</span>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Edit Button */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleEditClick(p)} 
                  className="flex-shrink-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={3}
              onPageChange={handlePageChange}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              showInfo={true}
              itemName="Partners"
            />
          </div>
        )}
      </div>

      {/* ── Add / Edit Partner Modal ── */}
      {showAddPartnerPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[990] p-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 border-b flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="font-bold text-gray-800">{editingPartnerId ? "Edit Delivery Partner" : "Add Delivery Partner"}</h3>
                <p className="text-xs text-gray-500">{editingPartnerId ? "Update driver details" : "Register a new fleet driver"}</p>
              </div>
              <button onClick={() => { setShowAddPartnerPopup(false); resetForm(); }} className="p-1.5 hover:bg-gray-200 rounded-md transition-colors text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
      
            <div className="p-5 overflow-y-auto space-y-4 text-sm">
              <div className="space-y-3">
                <div>
                  <label className="font-medium text-gray-700 block mb-1 text-xs">Full Name *</label>
                  <Input placeholder="Enter driver name" value={partnerForm.name} onChange={(e) => setPartnerForm({ ...partnerForm, name: e.target.value })} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Mobile Number *</label>
                    <Input placeholder="10-digit mobile" maxLength={10} value={partnerForm.mobile} onChange={(e) => setPartnerForm({ ...partnerForm, mobile: e.target.value.replace(/\D/g, '') })} />
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Email Address</label>
                    <Input type="email" placeholder="Email" value={partnerForm.email} onChange={(e) => setPartnerForm({ ...partnerForm, email: e.target.value })} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t pt-3">
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Vehicle Type *</label>
                    <select 
                      className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 bg-white" 
                      value={partnerForm.vehicle_type} 
                      onChange={(e) => setPartnerForm({ ...partnerForm, vehicle_type: e.target.value })}
                    >
                      <option value="">Select Vehicle Type</option>
                      {vehicles.map((vType, idx) => {
                        const value = typeof vType === 'string' ? vType : (vType.name || vType.type || "");
                        return (
                          <option key={idx} value={value}>
                            {value}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Vehicle Number</label>
                    <Input placeholder="e.g. MH 01 AB 1234" value={partnerForm.vehicle_number} onChange={(e) => setPartnerForm({ ...partnerForm, vehicle_number: e.target.value.toUpperCase() })} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Fleet *</label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={partnerForm.fleet} onChange={(e) => setPartnerForm({ ...partnerForm, fleet: e.target.value })}>
                      <option value="brickbucket">Brickbucket</option>
                      <option value="on demand">On Demand</option>
                      <option value="supplier">Supplier</option>
                      <option value="3 pl">3 PL</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-medium text-gray-700 block mb-1 text-xs">Platform *</label>
                    <select className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={partnerForm.platform} onChange={(e) => setPartnerForm({ ...partnerForm, platform: e.target.value })}>
                      <option value="brickbucket">Brickbucket</option>
                      <option value="own">Own</option>
                      <option value="ola">Ola</option>
                      <option value="uber">Uber</option>
                      <option value="rapido">Rapido</option>
                      <option value="shadowfax">Shadowfax</option>
                      <option value="porter">Porter</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
      
            <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-3 flex-shrink-0">
              <Button variant="outline" onClick={() => { setShowAddPartnerPopup(false); resetForm(); }}>Cancel</Button>
              <Button onClick={handleSavePartner} disabled={isSubmitting} className="bg-[#f7525a] text-white hover:bg-[#f7525a] min-w-[120px]">
                {isSubmitting ? 'Saving...' : (editingPartnerId ? 'Update Partner' : 'Save Partner')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactModal({ phone, onClose }) {
  if (!phone) return null;
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-5 flex flex-col min-w-[280px] animate-in zoom-in duration-200">
        <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-3">
          <h4 className="font-bold text-slate-800 text-sm">Contact Actions</h4>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="text-center mb-5">
          <div className="text-lg font-semibold text-blue-600 tracking-wide">{phone}</div>
        </div>
        <div className="flex flex-col gap-2.5">
          <button onClick={() => { navigator.clipboard.writeText(phone); onClose(); }} className="px-4 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium text-center transition-colors">
            Copy Number
          </button>
          <a href={`https://wa.me/${phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" onClick={onClose} className="px-4 py-2.5 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/20 rounded-lg text-sm text-[#1da850] font-bold text-center transition-colors">
            Message on WhatsApp
          </a>
          <a href={`tel:${phone}`} onClick={onClose} className="px-4 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-700 font-medium text-center transition-colors">
            Call Number
          </a>
        </div>
      </div>
    </div>
  );
}


function OrderNotifyModal({ order, onClose, showPopup }) {
  const [template, setTemplate] = useState("DELIVERY_DELAY");
  const [reason, setReason] = useState("Heavy Traffic / Logistics Delay");
  const [newEta, setNewEta] = useState("");
  const [customMsg, setCustomMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSend = async () => {
    setIsSubmitting(true);
    try {
      const token = decryptData(sessionStorage.getItem("token"));
      const res = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/notify-order-update `, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token || "" },
        body: JSON.stringify({
          order_id: order.order_id || order.id || order._id,
          template_key: template,
          new_eta: newEta || undefined,
          reason,
          custom_message: template === 'CUSTOM' ? customMsg : undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        showPopup("Notification sent via WhatsApp & In-App!", "success");
        onClose();
      } else {
        showPopup(data.message || "Failed to send notification", "error");
      }
    } catch (e) {
      showPopup(e.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-lg w-full p-6 space-y-4">
        <div className="flex justify-between items-center border-b pb-3">
          <h3 className="font-bold text-slate-800 text-sm">Notify Customer • Order {order.orderNumber}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500"><X className="w-4 h-4" /></button>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Notification Scenario</label>
          <select 
            value={template} 
            onChange={(e) => setTemplate(e.target.value)}
            className="w-full text-xs border rounded-lg p-2.5 bg-white border-slate-300"
          >
            <option value="DELIVERY_DELAY">Delivery Delay (ETA Change)</option>
            <option value="ITEM_UNAVAILABLE">Item Unavailable / Out of Stock</option>
            <option value="ETA_UPDATE">Standard ETA Reschedule</option>
            <option value="CUSTOM">Custom Message</option>
          </select>
        </div>

        {template === "DELIVERY_DELAY" && (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Delay Reason</label>
              <input 
                type="text" 
                value={reason} 
                onChange={(e) => setReason(e.target.value)} 
                className="w-full text-xs border rounded-lg p-2 border-slate-300" 
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Revised Delivery ETA</label>
              <input 
                type="datetime-local" 
                value={newEta} 
                onChange={(e) => setNewEta(e.target.value)} 
                className="w-full text-xs border rounded-lg p-2 border-slate-300" 
              />
            </div>
          </>
        )}

        {template === "CUSTOM" && (
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Message Content</label>
            <textarea 
              rows={3} 
              value={customMsg} 
              onChange={(e) => setCustomMsg(e.target.value)} 
              placeholder="Enter message for WhatsApp & In-App banner..." 
              className="w-full text-xs border rounded-lg p-2 border-slate-300 resize-none"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-slate-600 border rounded-lg hover:bg-slate-50">Cancel</button>
          <button 
            disabled={isSubmitting} 
            onClick={handleSend} 
            className="px-4 py-1.5 text-xs text-white font-medium rounded-lg bg-red-600 hover:bg-red-700"
          >
            {isSubmitting ? "Sending..." : "Send WhatsApp & In-App"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Root App                                                               */
/* ---------------------------------------------------------------------- */
export default function DeliveryManagementDashboard() {
  const [activeTab, setActiveTab] = useState("orders");

  const [orders,setOrders] = useState();
  const [shipments,setShipments] = useState();
  
  // Track state
  const [trackOrder, setTrackOrder] = useState(null);
  const [trackDg, setTrackDg] = useState(null);
  const [trackShipment, setTrackShipment] = useState(null);
  const [trackTrip, setTrackTrip] = useState(null);
  const [wizard, setWizard] = useState(null);

  const [contactPhone, setContactPhone] = useState(null);
  
  // Trips view state
  const [viewTripShipments, setViewTripShipments] = useState(null);
  const [viewTripLegs, setViewTripLegs] = useState(null);

  // Custom Trip Creation Flow from Shipments Screen
  const [createTripShipmentIds, setCreateTripShipmentIds] = useState(null);
  const [trips,setTrips] = useState();

  //Fleet Screen States
  const [fleets,setFleets] = useState([]);
  const [partners,setPartners] = useState([]);
  const [vehicles,setVehicles] = useState([]);

  const token = decryptData(sessionStorage.getItem("token"));

  const [loading, setLoading] = useState(true);

  const [globalPopup, setGlobalPopup] = useState({ show: false, message: "", type: "success", targetTab: null });

  const [notifyOrder, setNotifyOrder] = useState(null);

  const showPopup = (message, type = "success", targetTab = null) => {
    setGlobalPopup({ show: true, message, type, targetTab });
  };

  // Fetch only the data required for the currently active tab
  const loadDataForTab = async (tab) => {
    setLoading(true);
    try {
      if (tab === "orders") await fetchOrderToCreateShipments();
      if (tab === "shipments") await fetchShipments();
      if (tab === "trips") {
        // Fetch partners together with trips so TripWizard has partner data ready
        await Promise.all([fetchTrips(), fetchPartners()]);
      }
      if (tab === "fleet") await fetchPartners();
    } finally {
      setLoading(false);
    }
  };

  // Trigger on initial mount and whenever the active tab changes
  useEffect(() => {
    loadDataForTab(activeTab);
  }, [activeTab]);

  // Master refresh function for modals to trigger a reload of the current view
  const refreshDashboard = () => {
    loadDataForTab(activeTab);
  };

  useEffect(() => {
    window.handleGlobalPhoneClick = (phone) => setContactPhone(phone);
    return () => delete window.handleGlobalPhoneClick;
  }, []);

  // MASTER REFRESH FUNCTION
/*   const refreshDashboard = () => {
    fetchTrips();
    fetchPartners();
    fetchOrderToCreateShipments();
    fetchShipments();
  }; */

  useEffect(() => {
    refreshDashboard();
  }, []);

  // Fetch Trips
  const fetchTrips = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/trips`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token || "",
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setTrips(data.data || []);
      } else {
        setTrips(TRIPS);
      }
    } catch (error) {
      console.error("Error fetching trips:", error);
    }
  };

  // Fetch Delivery Partners
  const fetchPartners = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery-partner/get-all-delivery-partners`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token || "",
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setPartners(data.data?.partners || []);
        setFleets(data.data?.fleets || []);
        setVehicles(data.data?.vehicle_types || []);
      } else {
        console.error("Failed to fetch partners:", data);
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
    }
  };

  const fetchOrderToCreateShipments = async () => {
    try{
        const response = await fetch(
        `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/get-orders-to-create-shipments`,
        {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "x-token": token || "",
        },
        }
    );

    const data = await response.json();
    console.log("data:",data);

    if (data.success) {
        setOrders(data.data || []);
    } else {
        setOrders(ORDERS);
    }
    }catch(error){
      console.error("Error fetching Orders:", error);
    }
  };
  
  const fetchShipments = async () => {
    try{
            const response = await fetch(
          `${process.env.REACT_APP_FORE_BRICKBUCKET}delivery/shipments`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-token": token || "",
            },
          }
        );
  
        const data = await response.json();
  
        if (data.success) {
          setShipments(data.data || []);
        } else {
          setShipments(SHIPMENTS);
        }
    }catch(error){
        console.error("Error fetching Shipments:", error);
    }
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui" }}>
      <div className="max-w-full border rounded mx-auto px-6 py-4">
        <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        >
        <div className="flex flex-row justify-between mr-10">
        <TabsList>
        {TABS.map((t) => {
            const Icon = t.icon;

            return (
            <TabsTrigger
                key={t.key}
                value={t.key}
                className="rounded-md px-4 data-[state=active]:bg-gray-200 data-[state=active]:text-black data-[state=active]:shadow-sm"
            >
                {Icon && <Icon className="w-4 h-4 mr-1.5" />}
                {t.label}
            </TabsTrigger>
            );
        })}
        </TabsList>
        </div>
        {/* Pass onRefresh to tabs that need it */}
        {activeTab === "orders" && <OrdersScreen orders={orders} loading={loading} onTrackOrder={setTrackOrder} onTrackDg={(dg, order) => setTrackDg({ dg, orderInfo: order })} onRefresh={refreshDashboard} showPopup={showPopup} onNotifyOrder={setNotifyOrder}/>}
        {activeTab === "shipments" && <ShipmentsScreen shipments={shipments} loading={loading} onTrack={setTrackShipment} onCreateTrip={(ids) => setCreateTripShipmentIds(ids)} />}
        {activeTab === "trips" && <TripsScreen trips={trips} loading={loading} onTrack={setTrackTrip} onAssign={(selectedTrip) => setWizard({ mode: "assign", trip: selectedTrip })} onViewShipments={setViewTripShipments} onViewRouteLegs={setViewTripLegs} onPhoneClick={setContactPhone} />}
        {activeTab === "fleet" && <FleetScreen fleets={fleets} loading={loading} partners={partners} vehicles={vehicles} onRefresh={refreshDashboard} showPopup={showPopup}/>}
        </Tabs>  
      </div>

      {/* Render Popups/Modals based on active tracking object */}
      <OrderTrackPanel order={trackOrder} onClose={() => setTrackOrder(null)} onNotify={(order) => setNotifyOrder(order)}/>
      <DgTrackPanel dg={trackDg?.dg} orderInfo={trackDg?.orderInfo} onClose={() => setTrackDg(null)} />
      <ShipmentTrackPanel shipment={trackShipment} onClose={() => setTrackShipment(null)} />
      
      <TripTrackModal trip={trackTrip} onClose={() => setTrackTrip(null)} />
      <TripShipmentsModal trip={viewTripShipments} onClose={() => setViewTripShipments(null)} />
      <TripRouteLegsModal trip={viewTripLegs} onClose={() => setViewTripLegs(null)} />
      
      {/* Pass onRefresh to Modals */}
      {createTripShipmentIds && <CreateTripModal shipmentsData={shipments} selectedShipmentIds={createTripShipmentIds} onClose={() => setCreateTripShipmentIds(null)} onRefresh={refreshDashboard} showPopup={showPopup}/>}
      {wizard && <TripWizard mode={wizard.mode} partners={partners} trip={wizard.trip} onClose={() => setWizard(null)} onRefresh={refreshDashboard} showPopup={showPopup}/>}
      <ContactModal phone={contactPhone} onClose={() => setContactPhone(null)} />

      {notifyOrder && (<OrderNotifyModal order={notifyOrder} onClose={() => setNotifyOrder(null)} showPopup={showPopup}/>)}

      {/* ── Global Status Popup Overlay ── */}
      {globalPopup.show && (
              <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-sm w-full text-center animate-in zoom-in duration-200">
                  {globalPopup.type === 'success' ? (
                    <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                  ) : (
                    <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
                  )}
                  <h3 className="font-bold text-xl text-gray-900 mb-2">
                    {globalPopup.type === 'success' ? 'Success!' : 'Error'}
                  </h3>
                  <p className="text-gray-600 text-sm mb-6">{globalPopup.message}</p>
                  <Button 
                    className={`w-full ${globalPopup.type === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`} 
                    onClick={() => {
                      const target = globalPopup.targetTab;
                      setGlobalPopup({ show: false, message: "", type: "success", targetTab: null });
                      if (target) {
                        setActiveTab(target);
                      }
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )} 
    </div>
  );
}